/**
 * Resume Editor Modal Component
 * Requirements: 4.1, 4.2, 4.4, 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4, 2.5, 3.1, 3.2, 3.3, 3.4, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 7.1, 7.2, 7.3, 7.4
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../services/api';
import { X, Loader2, Save, Plus, Trash2, ChevronUp, ChevronDown, Edit2, Building2, Briefcase, Upload, Download, FileText, FileType, AlertTriangle, Sparkles, Check, XCircle, ExternalLink, Copy, Brain, Settings, SlidersHorizontal, Gauge, Clock, Eye, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { UpgradePromptModal } from './UpgradePromptModal';
import { ScorecardModal } from './ScorecardModal';
import { WritingSampleModal } from './WritingSampleModal';
import { SectionNavPanel, type SectionState } from './SectionNavPanel';
import { ResumePreviewPanel } from './ResumePreviewPanel';
import { buildScorecard, setTextAtPath, type FindingPath } from '../utils/scorecard';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { importResumeFile, ImportValidationError, ImportUploadError, ImportProcessError, ImportAuthError, ImportTimeoutError } from '../services/importService';
import type { ResumeJSON, Recommendation, ContactItem, Education, Award, Presentation, Publication, Portfolio, ExperienceCompany, ExperienceRole, ExperienceBullet, UserPreferences, SubcomponentType, GenerationType, SectionSettings, SynonymSet, Demographics, WritingFingerprint } from '../types';
import { GENERATION_TYPE_RESTRICTIONS, DEFAULT_SECTION_ORDER } from '../types';
import { GENERATION_TYPE_LABELS } from '../constants';
import yaml from 'js-yaml';
import { BulletTextWithLineBreaks, RESUME_FONT } from './BulletTextWithLineBreaks';

interface ResumeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  // savedName is the resume name that was just persisted. The parent should
  // refresh lists AND set its editing-resume state to savedName so a brand-new
  // resume transitions from create-mode to edit-mode without closing the modal.
  onSuccess: (savedName?: string) => void;
  resumename?: string;
  onDeleteResume?: (resumename: string) => Promise<void>;
  // Switch the editor to another of the user's resumes (Resume ▾ → "Switch
  // resumes"). The parent owns which resume is being edited, so it re-points
  // `resumename` (and its own selected-resume state) and the editor's
  // [isOpen, resumename] load effect reloads through the normal path.
  onSwitchResume?: (resumename: string) => void;
  // Start the app's existing "new resume" flow (quota checks + create-mode
  // editor). The editor closes first, then hands off.
  onNewResume?: () => void;
}

// Resume HTML typography constants — keep in sync with skins/default/style.css
// Page: max-width 8.5in, padding 0.6in each side → content width 7.3in = 700.8px
// Footer columns (education/awards/presentations): 50% of 700.8px ≈ 350px
// RESUME_FONT + RESUME_BULLET_WIDTH live in BulletTextWithLineBreaks (shared
// with the Scorecard); imported above so the bullet metrics stay in one place.
// Chrome for every top-level block in the editor's middle scroll pane
// (mockup 19 "rail TOC" `.sec`): a white card with a 1px gray border and
// rounded corners, so it is obvious where one section ends and the next
// begins. Wrapper only — no block's own content or layout depends on it.
const SECTION_CARD = 'rounded-[10px] border border-gray-200 bg-white p-4';

const RESUME_WIDTH = 771;          // 7.3in content area in px (+10% for wider lightbox)

// Per-item tag minimums the backend generator targets (ITEMS_TAG_COUNT_RANGES
// in shared/tag_status.py): an item below its minimum is (re)tagged on the
// next save; an item at/above it keeps its tags. These drive the per-item
// "tags generate on Save / generating" chips and the save-time poll trigger.
const TAG_MIN_BULLET = 10;
const TAG_MIN_ITEM = 5;

// Adopt freshly generated tags into the user's (possibly edited) in-editor
// state without clobbering edits made while generation ran. Items are matched
// by their text/title; an item the user reworded mid-generation is left alone
// (its tags regenerate on the next save anyway). Only below-minimum tag sets
// are filled — user-typed tags always win.
function mergeGeneratedTags(current: ResumeJSON, freshRaw: any): ResumeJSON {
  const fresh = freshRaw || {};
  const next: any = JSON.parse(JSON.stringify(current));
  const tagMap = (items: any[], keyField: string) => {
    const m = new Map<string, string[]>();
    for (const it of items || []) {
      const k = ((it?.[keyField] || '') as string).trim();
      if (k && Array.isArray(it?.tags) && it.tags.length && !m.has(k)) m.set(k, it.tags);
    }
    return m;
  };
  const skillMap = tagMap(fresh.skills, 'title');
  for (const s of next.skills || []) {
    const k = (s.title || '').trim();
    if ((s.tags?.length || 0) < TAG_MIN_ITEM && skillMap.has(k)) s.tags = [...skillMap.get(k)!];
  }
  const bulletMap = new Map<string, string[]>();
  for (const c of fresh.experience || [])
    for (const r of c?.roles || [])
      for (const b of r?.bullets || []) {
        const k = ((b?.text || '') as string).trim();
        if (k && Array.isArray(b?.tags) && b.tags.length && !bulletMap.has(k)) bulletMap.set(k, b.tags);
      }
  for (const c of next.experience || [])
    for (const r of c?.roles || [])
      for (const b of r?.bullets || []) {
        const k = (b?.text || '').trim();
        if ((b.tags?.length || 0) < TAG_MIN_BULLET && bulletMap.has(k)) b.tags = [...bulletMap.get(k)!];
      }
  for (const section of ['education', 'awards', 'presentations', 'publications', 'portfolios']) {
    const m = tagMap(fresh[section], 'title');
    for (const it of next[section] || []) {
      const k = (it.title || '').trim();
      if ((it.tags?.length || 0) < TAG_MIN_ITEM && m.has(k)) it.tags = [...m.get(k)!];
    }
  }
  return next as ResumeJSON;
}

// Default icon options — used as fallback until global settings are fetched
const DEFAULT_ICON_OPTIONS: { value: ContactItem['icon']; label: string; svg: string; placeholder: string }[] = [
  { value: 'email-at', label: 'Email', svg: '/icons/email-at.svg', placeholder: 'mailto:email@example.com' },
  { value: 'phone', label: 'Phone', svg: '/icons/phone.svg', placeholder: 'tel:+18889871234' },
  { value: 'phone-volume', label: 'Phone Alt', svg: '/icons/phone-volume.svg', placeholder: 'tel:+18889871234' },
  { value: 'linkedin', label: 'LinkedIn', svg: '/icons/linkedin.svg', placeholder: 'https://www.linkedin.com/in/username' },
  { value: 'x-twitter', label: 'X/Twitter', svg: '/icons/x-twitter.svg', placeholder: 'https://x.com/username' },
  { value: 'bluesky', label: 'Bluesky', svg: '/icons/bluesky.svg', placeholder: 'https://bsky.app/profile/username.bsky.social' },
  { value: 'github', label: 'GitHub', svg: '/icons/github.svg', placeholder: 'https://github.com/username' },
  { value: 'github-square', label: 'GitHub Square', svg: '/icons/github-square.svg', placeholder: 'https://github.com/username' },
  { value: 'globe', label: 'Website', svg: '/icons/globe.svg', placeholder: 'https://your.website.com' },
  { value: 'house', label: 'Home', svg: '/icons/house.svg', placeholder: 'https://www.google.com/maps/place/San+Francisco+Bay+Area' },
  { value: 'facebook', label: 'Facebook', svg: '/icons/facebook.svg', placeholder: 'https://www.facebook.com/username' },
  { value: 'instagram', label: 'Instagram', svg: '/icons/instagram.svg', placeholder: 'https://www.instagram.com/username' },
  { value: 'tiktok', label: 'TikTok', svg: '/icons/tiktok.svg', placeholder: 'https://www.tiktok.com/@username' },
  { value: 'youtube', label: 'YouTube', svg: '/icons/youtube.svg', placeholder: 'https://www.youtube.com/@username' },
  { value: 'discord', label: 'Discord', svg: '/icons/discord.svg', placeholder: 'https://discord.com/users/username' },
  { value: 'slack', label: 'Slack', svg: '/icons/slack.svg', placeholder: 'https://yourworkspace.slack.com/team/yourslackid' },
  { value: 'telegram', label: 'Telegram', svg: '/icons/telegram.svg', placeholder: 'https://t.me/username' },
  { value: 'whatsapp', label: 'WhatsApp', svg: '/icons/whatsapp.svg', placeholder: 'https://wa.me/phonenumber' },
  { value: 'signal-chat', label: 'Signal', svg: '/icons/signal-chat.svg', placeholder: 'https://signal.me/#u/username' },
  { value: 'skillsnap', label: 'SkillSnap', svg: '/icons/skillsnap.svg', placeholder: 'https://username.skillsnap.me' },
];

const GEN_TYPE_CONFIG: Record<GenerationType, { icon: React.ReactNode; className: string; tooltip: string }> = {
  manual: { icon: <Settings className="w-3.5 h-3.5 mr-1" />, className: 'bg-gray-200 text-gray-700 hover:bg-gray-300', tooltip: 'Manual: content appears on bespoke resumes exactly as written, 1:1' },
  embedding: { icon: <Brain className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-100 text-purple-700 hover:bg-purple-200', tooltip: 'AI Match: AI selects the most relevant items matched against the Job Description, without altering your content' },
  generate: { icon: <SlidersHorizontal className="w-3.5 h-3.5 mr-1" />, className: 'bg-purple-200 text-purple-800 hover:bg-purple-300', tooltip: 'AI Generate: AI fully generates new content tailored to the Job Description — double-check the results!' },
  omit: { icon: <XCircle className="w-3.5 h-3.5 mr-1" />, className: 'bg-red-100 text-red-700 hover:bg-red-200', tooltip: 'Omit: this section will not appear on bespoke resumes' },
};

const DEFAULT_GEN_TYPES: Record<SubcomponentType, GenerationType> = {
  contact: 'manual',
  summary: 'generate',
  skills: 'embedding',
  highlights: 'generate',
  experience: 'embedding',
  education: 'manual',
  awards: 'manual',
  presentations: 'manual',
  publications: 'manual',
  portfolios: 'manual',
  coverletter: 'generate',
  elevatorpitch: 'omit',
};

// Default heading for every section the editor renders. A user-supplied name
// (resumejson.section_settings.names) overrides it; clearing the name — or
// typing the default back — drops the override.
const SECTION_DEFAULT_LABELS: Record<string, string> = {
  contact: 'Contact Information',
  summary: 'Summary',
  highlights: 'Highlights',
  skills: 'Skills',
  experience: 'Experience',
  education: 'Education',
  awards: 'Awards',
  presentations: 'Presentations & Speaking',
  publications: 'Publications',
  portfolios: 'Portfolios',
  coverletter: 'Cover Letter',
};

// Custom section names are display labels; longer ones are dropped server-side
// (shared/section_settings.MAX_SECTION_NAME_LEN) — cap the input to match.
const MAX_SECTION_NAME_LEN = 80;

// Clean up a stored/legacy section order: keep only sections the editor renders,
// drop duplicates, append anything missing (new sections), and pin contact first.
function normalizeSectionOrder(raw?: SubcomponentType[] | null): SubcomponentType[] {
  const reorderable = DEFAULT_SECTION_ORDER.filter(s => s !== 'contact');
  const allowed = new Set<SubcomponentType>(reorderable);
  const seen = new Set<SubcomponentType>();
  const ordered: SubcomponentType[] = [];
  for (const s of raw || []) {
    if (allowed.has(s) && !seen.has(s)) { seen.add(s); ordered.push(s); }
  }
  for (const s of reorderable) {
    if (!seen.has(s)) { seen.add(s); ordered.push(s); }
  }
  return ['contact', ...ordered];
}

// Demographics option lists (voluntary EEO disclosure). These never appear on a
// generated resume — they only pre-fill applications via the Chrome Extension.
const YES_NO_OPTIONS = ['Yes', 'No'];
const GENDER_OPTIONS = ['Male', 'Female', 'Decline to Answer', 'Other'];
const RACE_OPTIONS = [
  'Hispanic/Latino', 'White', 'Black', 'Pacific Islander', 'Asian',
  'Native American', 'Multi-race', 'Decline to Answer', 'Other',
];
const VET_DIS_OPTIONS = ['Yes', 'No', 'Decline to Answer', 'Other'];

const emptyDemographics: Demographics = {
  location_country: 'USA',
  authorized_to_work: '',
  require_visa_sponsorship: '',
  gender: '',
  gender_other: '',
  race: '',
  race_other: '',
  veteran: '',
  veteran_other: '',
  disabled: '',
  disabled_other: '',
};

const emptyResume: ResumeJSON = {
  name: '',
  contact: [],
  summary: '',
  skills: [],
  highlights: [],
  experience: [],
  education: [],
  awards: [],
  presentations: [],
  publications: [],
  portfolios: [],
  summary_phrases: [],
  demographics: { ...emptyDemographics },
};

/**
 * Normalize a recommendation from old "replacement" format to new "action/data" format.
 * Backwards compatible: if rec has "replacement" but no "action", convert it.
 */
export function normalizeRec(rec: Recommendation): Recommendation {
  // 'advise' recommendations are informational: no replacement data, never
  // applied. Keep data null and never coerce the action to 'replace'.
  if (rec.action === 'advise') {
    return { ...rec, data: null };
  }
  if (rec.replacement && !rec.action) {
    const field = rec.field;
    let data: unknown;
    if (field in rec.replacement) {
      data = rec.replacement[field];
    } else {
      const vals = Object.values(rec.replacement);
      data = vals.length > 0 ? vals[0] : null;
    }
    return { ...rec, action: 'replace', data };
  }
  // Ensure action defaults to replace
  if (!rec.action) {
    return { ...rec, action: 'replace' };
  }
  return rec;
}

/**
 * Parse a JSON path into segments.
 * "experience[0].roles[1].bullets[2]" -> [{key:"experience",idx:0}, {key:"roles",idx:1}, {key:"bullets",idx:2}]
 */
function parsePath(path: string): { key: string; idx?: number }[] {
  const segments: { key: string; idx?: number }[] = [];
  const parts = path.split('.');
  for (const part of parts) {
    const match = part.match(/^(\w+)\[(\d+)\]$/);
    if (match) {
      segments.push({ key: match[1], idx: parseInt(match[2], 10) });
    } else {
      segments.push({ key: part });
    }
  }
  return segments;
}

/**
 * Navigate to a target in resume data by path, returning { current, parent, parentKey }.
 */
function navigatePath(obj: any, segments: { key: string; idx?: number }[]): { current: any; parent: any; parentKey: string | number | undefined } {
  let current: any = obj;
  let parent: any = null;
  let parentKey: string | number | undefined;
  for (const seg of segments) {
    if (current == null) return { current: null, parent, parentKey };
    parent = current;
    parentKey = seg.key;
    current = current[seg.key];
    if (current == null) return { current: null, parent, parentKey };
    if (seg.idx !== undefined) {
      if (!Array.isArray(current) || seg.idx >= current.length) return { current: null, parent, parentKey };
      parent = current;
      parentKey = seg.idx;
      current = current[seg.idx];
    }
  }
  return { current, parent, parentKey };
}

/**
 * Apply a recommendation action (replace/append/remove) at the given JSON path.
 * path format: "contacts[0]", "experience[0].roles[0].bullets[1]", "skills[3]", "summary"
 */
function applyActionAtPath(resumeData: ResumeJSON, path: string, field: string, action: string, data: unknown): ResumeJSON {
  const updated = JSON.parse(JSON.stringify(resumeData)) as ResumeJSON;
  const segments = parsePath(path);
  const { current, parent, parentKey } = navigatePath(updated, segments);

  if (current == null && parent == null) return updated;

  if (action === 'replace') {
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: set the field directly
      current[field] = data;
    } else if (parent != null && parentKey !== undefined) {
      // Scalar or direct target: replace the value at parent
      parent[parentKey] = data;
    }
  } else if (action === 'append') {
    const dataArr = Array.isArray(data) ? data : [data];
    if (current != null && typeof current === 'object' && !Array.isArray(current)) {
      // Object target: append to the field's array
      const existing = Array.isArray(current[field]) ? current[field] as unknown[] : [];
      const merged = [...existing];
      for (const item of dataArr) {
        if (!merged.includes(item)) merged.push(item);
      }
      current[field] = merged;
    } else if (parent != null && parentKey !== undefined) {
      // Array target: append items, coercing scalars into objects when the array
      // already contains objects (e.g., skills items must be {title, tags}).
      if (Array.isArray(current)) {
        const sample = current.length > 0 ? current[0] : null;
        const targetIsObjArr = sample != null && typeof sample === 'object';
        const labelField = targetIsObjArr
          ? ('title' in (sample as object) ? 'title' : 'text' in (sample as object) ? 'text' : 'name')
          : null;
        const merged = [...current];
        for (const item of dataArr) {
          let toAdd: unknown = item;
          if (targetIsObjArr && labelField && typeof item === 'string') {
            toAdd = { [labelField]: item, tags: [] };
          }
          if (typeof toAdd === 'object') {
            merged.push(toAdd);  // objects: skip reference-equality dedup
          } else if (!merged.includes(toAdd)) {
            merged.push(toAdd);
          }
        }
        parent[parentKey] = merged;
      }
    }
  } else if (action === 'remove') {
    const dataArr = Array.isArray(data) ? data : [data];
    const removeSet = new Set(dataArr.map(String));
    if (current != null && typeof current === 'object' && !Array.isArray(current) && Array.isArray((current as any)[field])) {
      // Object target with array field: remove items from current[field] by stringified value
      (current as any)[field] = ((current as any)[field] as unknown[]).filter(
        (item: unknown) => !removeSet.has(String(item))
      );
    } else if (Array.isArray(current) && parent != null && parentKey !== undefined) {
      // Array target: remove matching items.
      // For arrays of strings, match by stringified value.
      // For arrays of objects, match by item[field] (e.g., skills filtered by title).
      parent[parentKey] = current.filter((item: unknown) => {
        if (item != null && typeof item === 'object' && field) {
          return !removeSet.has(String((item as any)[field]));
        }
        return !removeSet.has(String(item));
      });
    } else if (Array.isArray(parent) && typeof parentKey === 'number') {
      // Item-in-array target (e.g., path "skills[9]"): remove the whole item from its parent array.
      (parent as unknown[]).splice(parentKey, 1);
    }
  }

  return updated;
}

// ---- Top/bottom chrome tokens (mirror the `20-section-rail` mockup) --------
/** Base metrics shared by every chrome button. */
const TB_BTN = 'inline-flex items-center gap-1.5 h-[34px] px-[11px] rounded-lg text-[13px] font-medium whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed';
/** Outlined button (Import / Export / Preview / Resume / Save). */
const TB_OUT = `${TB_BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400`;
/** Quiet button (Cancel / stepper). */
const TB_GHOST = `${TB_BTN} text-gray-700 hover:bg-gray-100`;
/** Filled sky button (Review resume / Save and Close). */
const TB_PRIMARY = `${TB_BTN} bg-primary-600 text-white hover:bg-primary-700`;
/** Dropdown surface. */
const TB_MENU = 'absolute top-full mt-1.5 right-0 z-30 min-w-[214px] bg-white border border-gray-200 rounded-[10px] p-[5px] shadow-[0_12px_28px_rgba(17,24,39,0.14),0_2px_6px_rgba(17,24,39,0.06)]';
const TB_MENU_ITEM = 'flex items-center gap-2.5 w-full px-2.5 py-[7px] rounded-md text-[13px] text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-left';
/** Thin vertical divider between chrome groups. */
const TB_VR = 'w-px h-6 bg-gray-200 flex-shrink-0 mx-[3px]';
/** Small uppercase group label inside a dropdown ("This resume" / "Switch resumes"). */
const TB_MENU_GROUP = 'px-2.5 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500';

/**
 * "edited 4 min ago" style metadata for a resume row in the Resume menu.
 * Returns null for a missing/unparseable timestamp so the row simply renders
 * without a subtitle rather than showing a bogus date.
 */
function editedAgoLabel(iso?: string): string | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 0) return 'edited just now';
  if (mins < 1) return 'edited just now';
  if (mins < 60) return `edited ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `edited ${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `edited ${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `edited ${weeks}w ago`;
  return `edited ${Math.floor(days / 365)}y ago`;
}

export function ResumeEditor({ isOpen, onClose, onSuccess, resumename, onDeleteResume, onSwitchResume, onNewResume }: ResumeEditorProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // Tag-generation status shown in the import notes box after an AI import.
  // 'generating' while the async tag pipeline runs, 'done' once tags land,
  // 'timeout' if it takes unusually long. Takes visual precedence over `warning`.
  const [tagGenPhase, setTagGenPhase] = useState<'idle' | 'generating' | 'done' | 'timeout'>('idle');
  const tagPollRef = useRef<{ cancelled: boolean } | null>(null);
  const [name, setName] = useState('');
  const [resume, setResume] = useState<ResumeJSON>(emptyResume);
  const [showScorecard, setShowScorecard] = useState(false);
  // Writing voice fingerprint (per-user; read-only via prefs). Drives the
  // "Provide Writing Sample" section chip above Summary.
  const [writingFingerprint, setWritingFingerprint] = useState<WritingFingerprint | null>(null);
  const [showWritingModal, setShowWritingModal] = useState(false);
  const [deletingFingerprint, setDeletingFingerprint] = useState(false);
  // Per-group "add skill" drafts, keyed by group id (or '' for ungrouped).
  const [newSkillByGroup, setNewSkillByGroup] = useState<Record<string, string>>({});
  // Drag-drop: index of the skill being dragged + the group currently hovered.
  const [draggedSkillIdx, setDraggedSkillIdx] = useState<number | null>(null);
  const [dragOverGroup, setDragOverGroup] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  // Synonym/skill conflict (task 8): which skill indices + synonym set ids clash.
  const [conflictSkillIdxs, setConflictSkillIdxs] = useState<Set<number>>(new Set());
  const [conflictSetIds, setConflictSetIds] = useState<Set<string>>(new Set());
  const [newSummaryPhrase, setNewSummaryPhrase] = useState('');

  // New contact item state
  const [newContactIcon, setNewContactIcon] = useState<ContactItem['icon']>('email-at');
  const [newContactTitle, setNewContactTitle] = useState('');
  const [newContactUrl, setNewContactUrl] = useState('');
  const [newSynonymTerms, setNewSynonymTerms] = useState('');
  const [synonymDrafts, setSynonymDrafts] = useState<Record<string, string>>({});
  const [newContactAutoFill, setNewContactAutoFill] = useState(false);
  const [editContactAutoFill, setEditContactAutoFill] = useState(false);
  
  // New education state
  const [newEduTitle, setNewEduTitle] = useState('');
  const [newEduOrg, setNewEduOrg] = useState('');
  const [newEduYear, setNewEduYear] = useState('');
  
  // New award state
  const [newAwardTitle, setNewAwardTitle] = useState('');
  const [newAwardIssuer, setNewAwardIssuer] = useState('');
  const [newAwardReward, setNewAwardReward] = useState('');
  const [newAwardYear, setNewAwardYear] = useState('');

  // New presentation state
  const [newPresentationTitle, setNewPresentationTitle] = useState('');
  const [newPresentationEvent, setNewPresentationEvent] = useState('');
  const [newPresentationYear, setNewPresentationYear] = useState('');

  // New publication state
  const [newPubTitle, setNewPubTitle] = useState('');
  const [newPubUrl, setNewPubUrl] = useState('');
  const [newPubYear, setNewPubYear] = useState('');

  // New portfolio state
  const [newPortTitle, setNewPortTitle] = useState('');
  const [newPortUrl, setNewPortUrl] = useState('');
  const [newPortYear, setNewPortYear] = useState('');

  // Experience editing state
  const [editingContactIndex, setEditingContactIndex] = useState<number | null>(null);
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null);
  const [editingSummaryPhraseIndex, setEditingSummaryPhraseIndex] = useState<number | null>(null);
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null);
  const [editingAwardIndex, setEditingAwardIndex] = useState<number | null>(null);
  const [editingPresentationIndex, setEditingPresentationIndex] = useState<number | null>(null);
  const [editingPublicationIndex, setEditingPublicationIndex] = useState<number | null>(null);
  const [editingPortfolioIndex, setEditingPortfolioIndex] = useState<number | null>(null);
  const [editingCompanyIndex, setEditingCompanyIndex] = useState<number | null>(null);
  const [editingRoleIndex, setEditingRoleIndex] = useState<{ companyIdx: number; roleIdx: number } | null>(null);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Clone state
  const [cloning, setCloning] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);
  // Full-resume export state ('pdf' | 'text' while a request is in flight)
  const [exporting, setExporting] = useState<null | 'pdf' | 'text'>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  // AI Review state
  const [reviewing, setReviewing] = useState(false);
  const [reviewComplete, setReviewComplete] = useState(false);
  const [editedReplacements, setEditedReplacements] = useState<Record<string, string>>({});
  const [showAiUpgradeModal, setShowAiUpgradeModal] = useState(false);

  // Master Resume URL state — always published (no opt-out)
  const [publishMasterResume, setPublishMasterResume] = useState(true);
  const [masterResumePath, setMasterResumePath] = useState('');
  const [otherResumePaths, setOtherResumePaths] = useState<{ resumename: string; path: string }[]>([]);

  // Bottom-left "Resume" actions menu (View HTML / View PDF / Clone / Delete)
  const [resumeMenuOpen, setResumeMenuOpen] = useState(false);
  const resumeMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!resumeMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (resumeMenuRef.current && !resumeMenuRef.current.contains(e.target as Node)) {
        setResumeMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [resumeMenuOpen]);

  // "Switch resumes" group inside that menu: the user's other resumes, loaded
  // from the SAME endpoint the header's resume selector uses (api.listResumes,
  // which returns name + lastupdate only — no resumejson). Fetched when the
  // menu opens so it is always current, and never on the editor's open path.
  const [switchResumes, setSwitchResumes] = useState<{ resumename: string; lastupdate?: string }[] | null>(null);
  const [switchLoading, setSwitchLoading] = useState(false);
  // A switch/new-resume action parked behind the unsaved-changes confirm.
  const [pendingNav, setPendingNav] = useState<{ kind: 'switch'; resumename: string } | { kind: 'new' } | null>(null);
  useEffect(() => {
    if (!resumeMenuOpen) return;
    let cancelled = false;
    setSwitchLoading(true);
    api.listResumes()
      .then(data => {
        if (cancelled) return;
        setSwitchResumes((data.resumes || []).map((r: any) => ({ resumename: r.resumename, lastupdate: r.lastupdate })));
      })
      .catch(() => { if (!cancelled) setSwitchResumes([]); })
      .finally(() => { if (!cancelled) setSwitchLoading(false); });
    return () => { cancelled = true; };
  }, [resumeMenuOpen]);
  const otherResumes = useMemo(
    () => (switchResumes || []).filter(r => r.resumename !== resumename),
    [switchResumes, resumename]
  );

  // Top "Export" menu (PDF / Text File / Data File)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!exportMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [exportMenuOpen]);

  // Top "Import" menu (Load from PDF / Load from file) — both open the same
  // file picker (it accepts .pdf / .yaml / .yml / .json).
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const importMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!importMenuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) {
        setImportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [importMenuOpen]);

  // Top-bar resume name: reads as inline text, becomes an input on click.
  // It is still the required "Resume Name" field — only the chrome changed.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  // Escape cancels a name edit; the ref stops the ensuing blur from committing.
  const cancelNameEditRef = useRef(false);

  function startEditName() {
    if (importing || loading) return;
    cancelNameEditRef.current = false;
    setNameDraft(name);
    setEditingName(true);
  }

  function commitName() {
    if (cancelNameEditRef.current) {
      cancelNameEditRef.current = false;
      setEditingName(false);
      return;
    }
    setName(nameDraft);
    setEditingName(false);
  }

  useEffect(() => {
    if (editingName) nameInputRef.current?.select();
  }, [editingName]);

  // "All recommendations implemented" banner: show ONCE after the user applies
  // all recommendations, then suppress on subsequent opens (persisted per-resume
  // in localStorage). A fresh AI Review re-arms it.
  const recsAckKey = resumename ? `ss_recs_implemented_ack_${resumename}` : '';
  const [recsImplementedAcked, setRecsImplementedAcked] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    try {
      setRecsImplementedAcked(!!recsAckKey && localStorage.getItem(recsAckKey) === '1');
    } catch {
      setRecsImplementedAcked(false);
    }
  }, [isOpen, recsAckKey]);

  // Contact icon options — loaded from global settings, falls back to hardcoded defaults
  const [iconOptions, setIconOptions] = useState(DEFAULT_ICON_OPTIONS);
  // Alpha-sort the contact icon selector by label so icons are easy to find by name.
  const sortedIconOptions = useMemo(
    () => [...iconOptions].sort((a, b) => a.label.localeCompare(b.label)),
    [iconOptions]
  );

  // ---- Per-resume section settings (part of the resume draft) --------------
  // Generation types, custom display names and section order live on the
  // resume at resumejson.section_settings and are persisted ONLY by Save,
  // like every other field in this lightbox.
  const [sectionGenTypes, setSectionGenTypes] = useState<Record<SubcomponentType, GenerationType>>({ ...DEFAULT_GEN_TYPES });
  const [sectionNames, setSectionNames] = useState<Partial<Record<SubcomponentType, string>>>({});
  const [sectionOrder, setSectionOrder] = useState<SubcomponentType[]>(() => normalizeSectionOrder(null));
  const [editingSectionTitle, setEditingSectionTitle] = useState<SubcomponentType | null>(null);
  const [sectionTitleDraft, setSectionTitleDraft] = useState('');
  // Escape cancels a title edit; the ref stops the ensuing blur from committing.
  const cancelSectionTitleRef = useRef(false);
  // The prefs fetch (legacy per-user defaults) and the resume load land
  // independently, so each stashes its half here and re-derives the draft.
  // Once the user touches a section setting the derive stops — their edits win.
  const prefSectionDefaultsRef = useRef<{ genTypes: Partial<Record<SubcomponentType, GenerationType>>; names: Partial<Record<SubcomponentType, string>>; order: SubcomponentType[] | null } | null>(null);
  const loadedSectionSettingsRef = useRef<SectionSettings | null>(null);
  const sectionSettingsTouchedRef = useRef(false);

  // Sections rendered in the reorderable middle of the editor (contact is pinned).
  const orderedSections = useMemo<SubcomponentType[]>(() => sectionOrder.filter(s => s !== 'contact'), [sectionOrder]);

  // ---- Right-hand section nav + live preview -------------------------------
  // The nav rail drives (and reflects) the SAME section-order / gen-type /
  // custom-name state the editor body and the save payload already use.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeNavKey, setActiveNavKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Scroll the editor pane to a nav row's block.
  function scrollToNavKey(key: string) {
    const root = scrollRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-nav-key="${key}"]`);
    if (!el) return;
    const top = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 8;
    root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    setActiveNavKey(key);
  }

  // Scrollspy: highlight the nav row whose block is at the top of the pane.
  // IntersectionObserver is only the trigger — the winner is recomputed from
  // live rects so a batch that omits an element can't pick a stale one.
  useEffect(() => {
    if (!isOpen || loading) return;
    const root = scrollRef.current;
    if (!root) return;
    // Nav keys in reading order — drives the footer's "Section N of M" stepper.
    const keys: string[] = [];
    root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => {
      if (el.dataset.navKey) keys.push(el.dataset.navKey);
    });
    setNavKeyOrder(prev => (prev.join('|') === keys.join('|') ? prev : keys));

    const recompute = () => {
      const rootTop = root.getBoundingClientRect().top;
      let best: string | null = null;
      let bestTop = -Infinity;
      root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => {
        const top = el.getBoundingClientRect().top - rootTop;
        // Topmost block whose start is at/above the 96px "reading line".
        if (top <= 96 && top > bestTop) { bestTop = top; best = el.dataset.navKey || null; }
      });
      if (!best) {
        const first = root.querySelector<HTMLElement>('[data-nav-key]');
        best = first?.dataset.navKey || null;
      }
      setActiveNavKey(prev => (prev === best ? prev : best));
    };
    const observer = new IntersectionObserver(recompute, { root, threshold: [0, 1] });
    root.querySelectorAll<HTMLElement>('[data-nav-key]').forEach(el => observer.observe(el));
    root.addEventListener('scroll', recompute, { passive: true });
    recompute();
    return () => {
      observer.disconnect();
      root.removeEventListener('scroll', recompute);
    };
  }, [isOpen, loading, orderedSections]);

  // Nav row status: omitted sections read as hollow, everything else is
  // "ready" once it has content. Highlights and the cover letter have no
  // authored content at all (they are written per job), so they count as
  // ready whenever they are not omitted.
  function sectionState(component: SubcomponentType): SectionState {
    if (sectionGenTypes[component] === 'omit') return 'omitted';
    const filled = (n: number) => (n > 0 ? 'ready' : 'empty') as SectionState;
    switch (component) {
      case 'contact': return filled((resume.contact || []).length);
      case 'summary': return filled((resume.summary || '').trim().length);
      case 'skills': return filled((resume.skills || []).length);
      case 'experience': return filled(getExperience().length);
      case 'education': return filled((resume.education || []).length);
      case 'awards': return filled((resume.awards || []).length);
      case 'presentations': return filled((resume.presentations || []).length);
      case 'publications': return filled((resume.publications || []).length);
      case 'portfolios': return filled((resume.portfolios || []).length);
      default: return 'ready';   // highlights / coverletter — generated per job
    }
  }

  // Status for the nav rows that are NOT resume sections (pinned rows), so the
  // rail can show an honest dot for every row it renders.
  function navRowState(key: string): SectionState {
    switch (key) {
      case 'basics':
        return (resume.name || '').trim() ? 'ready' : 'empty';
      case 'synonyms':
        return (resume.synonyms || []).some(s => (s.terms || []).filter(t => t.trim()).length >= 2) ? 'ready' : 'empty';
      case 'writingsample':
        return writingFingerprint ? 'ready' : 'empty';
      case 'demographics': {
        const d = resume.demographics || {};
        return Object.entries(d).some(([k, v]) => k !== 'location_country' && String(v || '').trim()) ? 'ready' : 'empty';
      }
      default:
        return sectionState(key as SubcomponentType);
    }
  }

  // "N of M sections ready" — M counts every printable section that is not
  // omitted, N counts those with content (or AI-generated per job).
  const navProgress = useMemo(() => {
    const printable: SubcomponentType[] = ['contact', ...orderedSections, 'coverletter'];
    let done = 0;
    let total = 0;
    for (const c of printable) {
      const state = sectionState(c);
      if (state === 'omitted') continue;
      total += 1;
      if (state === 'ready') done += 1;
    }
    return { done, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedSections, sectionGenTypes, resume]);

  // Deterministic Resume Scorecard overall — the headline number on the rail's
  // score bar and the top-bar Scorecard chip. Recomputed live on every edit
  // (client-side only, same builder the Scorecard modal uses).
  const scorecardScore = useMemo(() => buildScorecard(resume).overall, [resume]);

  // ---- Save state (bottom bar) --------------------------------------------
  // A snapshot of exactly what a Save would send; comparing it to the last
  // saved/loaded snapshot is what "Unsaved changes" means. Section settings are
  // part of the payload, so a gen-type cycle or a drag counts as a change.
  const saveSnapshot = useMemo(
    () => JSON.stringify({ n: name.trim(), p: masterResumePath, r: withSectionSettings(resume) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, masterResumePath, resume, sectionGenTypes, sectionNames, sectionOrder],
  );
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [savedClockTick, setSavedClockTick] = useState(0);
  // Baseline re-arms after every load/save; the effect below then captures the
  // snapshot once the derived section settings have settled. `baselineNonce` is
  // what a keep-open Save bumps: the ref alone can't re-run the effect, and a
  // save that leaves the payload byte-identical (the normal case — we just sent
  // it) produces the SAME `saveSnapshot` string, so without the nonce the effect
  // never re-fires and the footer stays stuck on "Unsaved changes".
  const baselineArmedRef = useRef(false);
  const [baselineNonce, setBaselineNonce] = useState(0);

  useEffect(() => {
    if (!isOpen || loading) return;
    if (baselineArmedRef.current) return;
    // Debounce: the section-settings derive lands a tick after the load, so
    // wait for the snapshot to stop moving before calling it "saved".
    const t = setTimeout(() => {
      baselineArmedRef.current = true;
      setSavedSnapshot(saveSnapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [isOpen, loading, saveSnapshot, baselineNonce]);

  // Re-arm on open/close so a reopened editor starts clean.
  useEffect(() => {
    if (isOpen) return;
    baselineArmedRef.current = false;
    setSavedSnapshot(null);
    setLastSavedAt(null);
  }, [isOpen]);

  // Keep the "N minutes ago" label honest while the editor stays open.
  useEffect(() => {
    if (!isOpen || lastSavedAt === null) return;
    const id = setInterval(() => setSavedClockTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, [isOpen, lastSavedAt]);

  const isDirty = savedSnapshot !== null && saveSnapshot !== savedSnapshot;
  const lastSavedLabel = useMemo(() => {
    if (lastSavedAt === null) return null;
    const mins = Math.floor((Date.now() - lastSavedAt) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSavedAt, savedClockTick]);

  // ---- Bottom-bar section stepper -----------------------------------------
  // Nav keys in the editor's own DOM order (kept in sync by the scrollspy
  // effect), so "Section N of M" and Previous/Next match what the user reads.
  const [navKeyOrder, setNavKeyOrder] = useState<string[]>([]);
  const navKeyLabel = (key: string): string => {
    switch (key) {
      case 'basics': return 'Basics';
      case 'synonyms': return 'Synonyms';
      case 'writingsample': return 'Writing sample';
      case 'demographics': return 'Demographics';
      default: return sectionLabel(key as SubcomponentType);
    }
  };
  const navIndex = activeNavKey ? navKeyOrder.indexOf(activeNavKey) : -1;

  // Fetch contact icon list from global settings on mount
  useEffect(() => {
    api.getPublicSetting('contact_icon_list')
      .then(data => {
        if (Array.isArray(data.setting_value) && data.setting_value.length > 0) {
          setIconOptions(data.setting_value);
        }
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  // Cancel any in-flight tag-generation poll loop on unmount.
  useEffect(() => {
    return () => {
      if (tagPollRef.current) tagPollRef.current.cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError(null); // Clear any previous errors when modal opens
      // Reset the section-settings draft so a freshly opened resume derives
      // from its own settings (and the legacy prefs) rather than the last one.
      sectionSettingsTouchedRef.current = false;
      prefSectionDefaultsRef.current = null;
      loadedSectionSettingsRef.current = null;
      setEditingSectionTitle(null);
      loadSectionGenTypes();
      if (resumename) {
        loadResume();
      } else {
        loadedSectionSettingsRef.current = {};
        deriveSectionSettings();
        setName('');
        setResume(emptyResume);
        setPublishMasterResume(true);
        setMasterResumePath('');
        setOtherResumePaths([]);
      }
    }
  }, [isOpen, resumename]);

  // NOTE: ESC-to-close is intentionally NOT wired for the Edit Resume lightbox.
  // It's a large form and an accidental ESC losing in-progress edits is more
  // annoying than helpful; users close via Cancel / Save and Close. (The
  // Scorecard sub-modal keeps its own ESC handler.)

  // Click outside skill to close editing
  useEffect(() => {
    if (editingSkillIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-skill-index="${editingSkillIndex}"]`)) {
        setEditingSkillIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSkillIndex]);

  // Once a synonym/skill conflict has been flagged (on a blocked save), re-evaluate
  // live as the user edits skills/synonyms so the red highlight clears the moment
  // the clash is resolved. Only runs while a conflict is currently flagged.
  useEffect(() => {
    if (conflictSkillIdxs.size === 0 && conflictSetIds.size === 0) return;
    const c = computeSynonymConflicts(resume);
    setConflictSkillIdxs(c.skillIdxs);
    setConflictSetIds(c.setIds);
    if (c.setIds.size === 0) setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume.skills, resume.synonyms]);

  // Click outside summary phrase to close editing
  useEffect(() => {
    if (editingSummaryPhraseIndex === null) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`[data-phrase-index="${editingSummaryPhraseIndex}"]`)) {
        setEditingSummaryPhraseIndex(null);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [editingSummaryPhraseIndex]);

  // Legacy per-user defaults — the fallback layer under this resume's own
  // section_settings (matches the backend's per-job > resume > user pref >
  // global default resolution).
  async function loadSectionGenTypes() {
    try {
      const prefs = await api.getPreferences();
      const genTypes: Partial<Record<SubcomponentType, GenerationType>> = {};
      const sections: SubcomponentType[] = ['contact', 'summary', 'skills', 'highlights', 'experience', 'education', 'awards', 'presentations', 'publications', 'portfolios', 'coverletter'];
      for (const s of sections) {
        const key = `default_gen_${s}` as keyof UserPreferences;
        if (prefs[key]) genTypes[s] = prefs[key] as GenerationType;
      }
      let names: Partial<Record<SubcomponentType, string>> = {};
      try {
        const parsed = prefs.section_names ? JSON.parse(prefs.section_names) : {};
        if (parsed && typeof parsed === 'object') names = parsed as Partial<Record<SubcomponentType, string>>;
      } catch { /* malformed legacy pref — fall through to defaults */ }
      let order: SubcomponentType[] | null = null;
      try {
        const parsed = prefs.section_order ? JSON.parse(prefs.section_order) as SubcomponentType[] : [];
        if (Array.isArray(parsed) && parsed.length > 0) order = parsed;
      } catch { /* malformed legacy pref — fall through to defaults */ }
      prefSectionDefaultsRef.current = { genTypes, names, order };
      setWritingFingerprint(prefs.writing_fingerprint ?? null);
    } catch {
      prefSectionDefaultsRef.current = { genTypes: {}, names: {}, order: null };
    }
    deriveSectionSettings();
  }

  // Recompute the section-settings draft from whichever sources have landed:
  // this resume's section_settings first, then the legacy user prefs, then the
  // built-in defaults. Skipped once the user has edited a section setting.
  function deriveSectionSettings() {
    if (sectionSettingsTouchedRef.current) return;
    const prefDefaults = prefSectionDefaultsRef.current;
    const fromResume = loadedSectionSettingsRef.current || {};
    setSectionGenTypes({
      ...DEFAULT_GEN_TYPES,
      ...(prefDefaults?.genTypes || {}),
      ...(fromResume.gen_types || {}),
    });
    // Legacy per-user custom names prefill the draft the same way gen types and
    // order do, so the editor shows what the backend actually renders today —
    // and the user's next Save migrates them onto this resume.
    setSectionNames({
      ...(prefDefaults?.names || {}),
      ...(fromResume.names || {}),
    });
    const rawOrder = (fromResume.order && fromResume.order.length > 0)
      ? fromResume.order
      : (prefDefaults?.order || null);
    setSectionOrder(normalizeSectionOrder(rawOrder));
  }

  // The section-settings draft is now user-owned — stop re-deriving it from the
  // loaded resume / prefs so an in-flight fetch can't clobber their edit.
  function touchSectionSettings() {
    sectionSettingsTouchedRef.current = true;
  }

  // Complete, self-contained section settings for the save payload: every
  // section's resolved generation type, only genuinely customized names, and
  // the full order.
  function buildSectionSettings(): SectionSettings {
    const names: Partial<Record<SubcomponentType, string>> = {};
    for (const key of Object.keys(sectionNames) as SubcomponentType[]) {
      const value = (sectionNames[key] || '').trim();
      if (value && value !== SECTION_DEFAULT_LABELS[key]) names[key] = value;
    }
    return {
      gen_types: { ...sectionGenTypes },
      names,
      order: [...sectionOrder],
    };
  }

  // Attach the current section-settings draft to a resumejson about to be saved.
  function withSectionSettings(rj: ResumeJSON): ResumeJSON {
    return { ...rj, section_settings: buildSectionSettings() };
  }

  function sectionLabel(component: SubcomponentType): string {
    return (sectionNames[component] || '').trim() || SECTION_DEFAULT_LABELS[component] || component;
  }

  function startEditSectionTitle(component: SubcomponentType) {
    cancelSectionTitleRef.current = false;
    setSectionTitleDraft(sectionLabel(component));
    setEditingSectionTitle(component);
  }

  function commitSectionTitle(component: SubcomponentType) {
    if (cancelSectionTitleRef.current) {
      cancelSectionTitleRef.current = false;
      return;
    }
    const value = sectionTitleDraft.trim();
    setSectionNames(prev => {
      const next = { ...prev };
      if (!value || value === SECTION_DEFAULT_LABELS[component]) delete next[component];
      else next[component] = value;
      return next;
    });
    touchSectionSettings();
    setEditingSectionTitle(null);
  }

  function cancelSectionTitle() {
    cancelSectionTitleRef.current = true;
    setEditingSectionTitle(null);
  }

  function moveSection(component: SubcomponentType, direction: 'up' | 'down') {
    const reorderable = [...orderedSections];
    const idx = reorderable.indexOf(component);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= reorderable.length) return;
    [reorderable[idx], reorderable[swapIdx]] = [reorderable[swapIdx], reorderable[idx]];
    setSectionOrder(['contact', ...reorderable]);
    touchSectionSettings();
  }

  // Reload just the writing fingerprint (after analyze / delete).
  async function reloadWritingFingerprint() {
    try {
      const prefs = await api.getPreferences();
      setWritingFingerprint(prefs.writing_fingerprint ?? null);
    } catch { /* keep current */ }
  }

  async function handleDeleteFingerprint() {
    if (!window.confirm('Remove your voice fingerprint? AI-generated sections will revert to the default voice.')) return;
    setDeletingFingerprint(true);
    try {
      await api.deleteWritingFingerprint();
      await reloadWritingFingerprint();
    } catch { /* best-effort */ } finally {
      setDeletingFingerprint(false);
    }
  }

  function cycleSectionGenType(component: SubcomponentType) {
    const current = sectionGenTypes[component];
    const allowed = GENERATION_TYPE_RESTRICTIONS[component] || ['manual', 'embedding', 'generate', 'omit'];
    const idx = allowed.indexOf(current);
    const next = allowed[(idx + 1) % allowed.length];
    // Draft-only — persisted with the resume on Save, not as a user preference.
    setSectionGenTypes({ ...sectionGenTypes, [component]: next });
    touchSectionSettings();
  }

  function renderGenTypeButton(component: SubcomponentType) {
    const genType = sectionGenTypes[component];
    const config = GEN_TYPE_CONFIG[genType];
    return (
      <button
        type="button"
        onClick={() => cycleSectionGenType(component)}
        data-help-target={`mainapp.resumes.resumeN.${component}.gentype`}
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${config.className} transition-colors`}
        title={`${config.tooltip}\n\nClick to change. This applies to ALL jobs generated from THIS resume (saved with the resume).`}
      >
        {config.icon}
        {GENERATION_TYPE_LABELS[genType]}
      </button>
    );
  }

  // Section heading: editable display name + generation-type chip, plus
  // up/down arrows for the reorderable sections (contact is pinned first,
  // cover letter pinned last with a fixed title).
  function renderSectionHeader(
    component: SubcomponentType,
    marginClass: 'mb-1' | 'mb-2',
    options: { editableTitle?: boolean } = {},
  ) {
    const { editableTitle = true } = options;
    const idx = orderedSections.indexOf(component);
    const reorderable = idx >= 0;
    const defaultLabel = SECTION_DEFAULT_LABELS[component] || component;
    return (
      <div className={`flex items-center gap-2 ${marginClass}`}>
        {!editableTitle ? (
          <label className="block text-base font-bold text-gray-700">{sectionLabel(component)}</label>
        ) : editingSectionTitle === component ? (
          <input
            type="text"
            autoFocus
            value={sectionTitleDraft}
            onChange={(e) => setSectionTitleDraft(e.target.value)}
            onBlur={() => commitSectionTitle(component)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitSectionTitle(component); }
              else if (e.key === 'Escape') { e.preventDefault(); cancelSectionTitle(); }
            }}
            aria-label={`${defaultLabel} section title`}
            maxLength={MAX_SECTION_NAME_LEN}
            className="text-base font-bold text-gray-700 px-1.5 py-0.5 border border-gray-300 rounded"
            autoComplete="off"
          />
        ) : (
          <button
            type="button"
            onClick={() => startEditSectionTitle(component)}
            data-help-target={`mainapp.resumes.resumeN.${component}.title`}
            className="group flex items-center gap-1 text-base font-bold text-gray-700 hover:text-primary-600"
            title={`Click to rename this section — the name is used on resumes generated from THIS resume. Default: ${defaultLabel}`}
          >
            {sectionLabel(component)}
            <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
        {renderGenTypeButton(component)}
        {reorderable && (
          <div className="flex items-center" data-help-target={`mainapp.resumes.resumeN.${component}.reorder`}>
            <button
              type="button"
              onClick={() => moveSection(component, 'up')}
              disabled={idx === 0}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label={`Move ${defaultLabel} up`}
              title="Move this section up (on the editor and on generated resumes)"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => moveSection(component, 'down')}
              disabled={idx === orderedSections.length - 1}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
              aria-label={`Move ${defaultLabel} down`}
              title="Move this section down (on the editor and on generated resumes)"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // Normalize a loaded resumejson in place: fill missing sections, coerce
  // legacy shapes, and guarantee every taggable item has a tags array.
  function normalizeResumeJson(loadedResume: any) {
    // Ensure required structure exists with defaults
    if (!loadedResume.contact || !Array.isArray(loadedResume.contact)) {
      loadedResume.contact = [];
    }
    if (!loadedResume.experience) {
      loadedResume.experience = [];
    }
    if (!loadedResume.education) {
      loadedResume.education = [];
    }
    if (!loadedResume.skills) {
      loadedResume.skills = [];
    }
    if (!loadedResume.awards) {
      loadedResume.awards = [];
    }
    if (!loadedResume.presentations) {
      loadedResume.presentations = [];
    }
    if (!loadedResume.publications) {
      loadedResume.publications = [];
    }
    if (!loadedResume.portfolios) {
      loadedResume.portfolios = [];
    }
    if (!loadedResume.highlights) {
      loadedResume.highlights = [];
    }
    if (loadedResume.summary === undefined) {
      loadedResume.summary = '';
    }

    // Ensure skills are Skill objects
    if (loadedResume.skills.length > 0 && typeof loadedResume.skills[0] === 'string') {
      loadedResume.skills = (loadedResume.skills as any as string[]).map((s: string) => ({ title: s, tags: [] }));
    }
    // Alpha-sort skills on load only, so they're easy to find by name. New skills
    // added in-session append to the bottom (see addSkillToGroup); the next save+reload
    // re-runs this normalize pass and re-sorts them into place.
    loadedResume.skills.sort((a: any, b: any) =>
      (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
    );
    // Ensure experience url is always an array
    if (loadedResume.experience) {
      for (const company of loadedResume.experience) {
        if (!Array.isArray(company.url)) {
          company.url = company.url ? [company.url as any] : [];
        }
      }
    }
    // Sanitize experience data — ensure all bullets have tags array
    if (loadedResume.experience) {
      for (const company of loadedResume.experience) {
        if (!company.roles) continue;
        for (const role of company.roles) {
          if (!role.bullets) role.bullets = [];
          role.bullets = role.bullets.map((b: any) => ({
            text: b.text || '',
            tags: Array.isArray(b.tags) ? b.tags : []
          }));
        }
      }
    }
    // Ensure education items have tags
    for (const edu of loadedResume.education) {
      if (!Array.isArray(edu.tags)) edu.tags = [];
    }
    // Ensure award items have tags
    for (const award of loadedResume.awards) {
      if (!Array.isArray(award.tags)) award.tags = [];
    }
    // Ensure presentation items have tags
    for (const pres of loadedResume.presentations) {
      if (!Array.isArray(pres.tags)) pres.tags = [];
    }
    // Ensure publication items have tags
    for (const pub of loadedResume.publications) {
      if (!Array.isArray(pub.tags)) pub.tags = [];
    }
    // Ensure portfolio items have tags
    for (const port of loadedResume.portfolios) {
      if (!Array.isArray(port.tags)) port.tags = [];
    }
    // Ensure summary_phrases exists and items have tags (backward compat)
    if (!Array.isArray(loadedResume.summary_phrases)) loadedResume.summary_phrases = [];
    for (const phrase of loadedResume.summary_phrases) {
      if (!Array.isArray(phrase.tags)) phrase.tags = [];
    }
    // Ensure demographics object exists with defaults (backward compat — older
    // resumes predate this optional, extension-only section).
    loadedResume.demographics = { ...emptyDemographics, ...(loadedResume.demographics || {}) };
    return loadedResume;
  }

  // Count tags across every taggable item — used to detect when the async
  // tag pipeline has written tags into the record (one-shot, not trickled).
  function countItemTags(rj: any): number {
    if (!rj) return 0;
    let n = 0;
    for (const company of rj.experience || []) {
      for (const role of company.roles || []) {
        for (const b of role.bullets || []) n += (b.tags?.length || 0);
      }
    }
    for (const s of rj.skills || []) n += (s.tags?.length || 0);
    for (const section of ['education', 'awards', 'presentations', 'publications', 'portfolios']) {
      for (const it of rj[section] || []) n += (it.tags?.length || 0);
    }
    return n;
  }

  // True if a save would trigger the backend to actually add tags — i.e. some
  // taggable item is below the per-item minimum the generator targets
  // (experience bullets: TAG_MIN_BULLET, all other sections: TAG_MIN_ITEM).
  // Mirrors the backend's ITEMS_TAG_COUNT_RANGES minimums so we only show the
  // "generating" status and poll when tags will really change (not on every
  // routine edit-and-save).
  function needsTagGeneration(rj: any): boolean {
    if (!rj) return false;
    for (const company of rj.experience || []) {
      for (const role of company.roles || []) {
        for (const b of role.bullets || []) {
          if ((b.text || '').trim() && (b.tags?.length || 0) < TAG_MIN_BULLET) return true;
        }
      }
    }
    for (const s of rj.skills || []) {
      if ((s.title || '').trim() && (s.tags?.length || 0) < TAG_MIN_ITEM) return true;
    }
    for (const section of ['education', 'awards', 'presentations', 'publications', 'portfolios']) {
      for (const it of rj[section] || []) {
        if ((it.title || '').trim() && (it.tags?.length || 0) < TAG_MIN_ITEM) return true;
      }
    }
    return false;
  }

  // Poll the record until the async tag pipeline finishes (resume_tags ===
  // 'generated', or item-tag count grows past the baseline), then bring the
  // generated tags into the editor. Started after a save that will (re)tag
  // items, and on open when a previous save's generation is still in flight.
  // `baselineJson` is the stringified resume state at poll start: if the user
  // hasn't edited since, the fresh record replaces the state wholesale
  // (captures synonym expansion + skill groups too); if they have, only the
  // generated tags are merged in so their edits survive.
  function startTagGenPolling(resumeName: string, baselineTagCount: number, baselineJson?: string) {
    // Cancel any prior poll loop.
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    const ctrl = { cancelled: false };
    tagPollRef.current = ctrl;
    const startedAt = Date.now();
    const MAX_WAIT_MS = 120000; // matches the gen_tags Lambda timeout
    const INTERVAL_MS = 4000;

    const tick = async () => {
      if (ctrl.cancelled) return;
      try {
        const data = await api.getResume(resumeName);
        if (ctrl.cancelled) return;
        const done = data.resume_tags === 'generated'
          || countItemTags(data.resumejson) > baselineTagCount;
        if (done) {
          setResume((prev) => {
            if (!baselineJson || JSON.stringify(prev) === baselineJson) {
              return normalizeResumeJson(data.resumejson);
            }
            return mergeGeneratedTags(prev, data.resumejson);
          });
          setName(resumeName);
          setPublishMasterResume(true);
          setMasterResumePath(data.master_resume_path || '');
          setTagGenPhase('done');
          return;
        }
      } catch {
        // transient — keep polling until the timeout
      }
      if (ctrl.cancelled) return;
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        setTagGenPhase('timeout');
        return;
      }
      setTimeout(tick, INTERVAL_MS);
    };
    setTimeout(tick, INTERVAL_MS);
  }

  // Pick a resume name not already in use, based on `base` (the imported
  // candidate name). Lets repeated imports create "Name", "Name (2)", ...
  // instead of failing the create with "already exists".
  async function deriveUniqueResumeName(base: string): Promise<string> {
    let existing: string[] = [];
    try {
      const data = await api.listResumes();
      existing = (data.resumes || []).map((r: any) => (r.resumename || '').toLowerCase());
    } catch {
      return base; // if the list fails, fall back to the base name
    }
    if (!existing.includes(base.toLowerCase())) return base;
    for (let i = 2; i < 100; i++) {
      const candidate = `${base} (${i})`;
      if (!existing.includes(candidate.toLowerCase())) return candidate;
    }
    return `${base} (${Date.now()})`;
  }

  async function loadResume() {
    if (!resumename) return;
    // Freshly loaded data becomes the new "saved" baseline for the bottom bar.
    baselineArmedRef.current = false;
    setLoading(true);
    try {
      const data = await api.getResume(resumename);
      setName(resumename);

      // Validate response structure
      if (!data || !data.resumejson) {
        console.error('Invalid resume data:', data);
        setError('Resume data is missing or corrupted. Please try again.');
        return;
      }

      const loadedResume = data.resumejson;

      normalizeResumeJson(loadedResume);
      setResume(loadedResume);

      // Per-resume section settings seed the draft (falling back to the legacy
      // user prefs for anything this resume doesn't define).
      loadedSectionSettingsRef.current = loadedResume.section_settings || {};
      deriveSectionSettings();

      // A previous save's tag generation may still be in flight (the save
      // Lambda sets resume_tags='generating'; gen_tags flips it to
      // 'generated'). Surface it and poll so a user who saved-and-closed sees
      // their tags land instead of silent blank tag boxes on reopen.
      if (data.resume_tags === 'generating' && needsTagGeneration(loadedResume)) {
        setTagGenPhase('generating');
        startTagGenPolling(resumename, countItemTags(loadedResume), JSON.stringify(loadedResume));
      }

      // Load master resume settings — always published, no opt-out
      setPublishMasterResume(true);
      setMasterResumePath(data.master_resume_path || '');

      // Fetch other resumes to detect duplicate paths
      try {
        const listData = await api.listResumes();
        const others = (listData.resumes || [])
          .filter((r: any) => r.resumename !== resumename && r.master_resume_path !== undefined)
          .map((r: any) => ({ resumename: r.resumename, path: r.master_resume_path || '' }));
        setOtherResumePaths(others);
      } catch { /* non-critical */ }
    } catch (err: any) {
      console.error('Failed to load resume:', err);
      if (err.response?.status === 404) {
        setError(`Resume "${resumename}" not found. It may have been deleted.`);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.message) {
        setError(`Failed to load resume: ${err.message}`);
      } else {
        setError('Failed to load resume. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(closeOnSuccess: boolean = true, resumeOverride?: ResumeJSON, nameOverride?: string) {
    // resumeOverride lets callers (e.g. auto-save right after PDF import) save
    // freshly-loaded data without waiting for the setResume state commit.
    // nameOverride lets the import auto-save name a brand-new resume (whose
    // name field is still empty) without waiting for the setName commit.
    // Section settings (generation types, custom names, order) are part of the
    // resume draft and persist only here, on Save.
    const resumeToSave = withSectionSettings(resumeOverride ?? resume);
    const savedName = (nameOverride ?? name).trim();
    if (!savedName) {
      setError('Resume name is required');
      return;
    }
    if (!resumeToSave.name) {
      setError('Your full professional name is required');
      return;
    }
    if (nameOverride && nameOverride.trim() && nameOverride.trim() !== name) {
      setName(nameOverride.trim());
    }

    // Block on synonym/skill conflicts (task 8): two skills that map to the same
    // synonym set would both swap to the winner on a tailored resume and show as
    // duplicates. Flag both sides red and explain the fix; don't save.
    const conflicts = computeSynonymConflicts(resumeToSave);
    if (conflicts.setIds.size > 0) {
      setConflictSkillIdxs(conflicts.skillIdxs);
      setConflictSetIds(conflicts.setIds);
      const firstSet = (resumeToSave.synonyms || []).find(s => conflicts.setIds.has(s.id));
      const clashSkills = resumeToSave.skills
        .filter((_, i) => conflicts.skillIdxs.has(i))
        .map(s => s.title);
      const a = clashSkills[0] || (firstSet?.terms?.[0] ?? 'one skill');
      const b = clashSkills[1] || (firstSet?.terms?.[1] ?? 'another skill');
      const win = firstSet?.terms?.[0] ?? b;
      setError(
        `You've set "${a}" and "${b}" as interchangeable in your synonyms, but ALSO ` +
        `defined them both in your skills. This could lead to duplication on a tailored ` +
        `resume — showing "${a}" next to "${a}" (replaced as a synonym of "${win}"). ` +
        `Easy solution: you can either remove the synonym, or better yet, remove one of ` +
        `the skills — we'll automatically show the synonym that best matches each job.`
      );
      return;
    }
    setConflictSkillIdxs(new Set());
    setConflictSetIds(new Set());

    setSaving(true);
    setError(null);
    try {
      if (resumename) {
        // Pass new name if it changed (for rename support)
        await api.updateResume(
          resumename, resumeToSave,
          savedName !== resumename ? savedName : undefined,
          masterResumePath,
          publishMasterResume,
        );
      } else {
        await api.createResume(savedName, resumeToSave, masterResumePath, publishMasterResume);
      }
      // Tell the parent what was saved. The parent refreshes its lists AND
      // sets its editing-resume state to savedName, so a brand-new resume
      // transitions to edit-mode (prop becomes defined) without the modal
      // closing — a subsequent Save then updates instead of creating a dup.
      onSuccess(savedName);
      // Bottom bar: this is now the saved state (re-armed so a follow-up
      // reload re-captures the server's version of the record).
      setLastSavedAt(Date.now());
      setSavedClockTick(t => t + 1);
      if (closeOnSuccess) {
        onClose();
      } else if (needsTagGeneration(resumeToSave)) {
        // Keep-open save where the backend will (re)generate tags. Show the
        // status and poll until tags land, then load the tagged record — this
        // is what makes freshly-imported / under-tagged resumes show their
        // tags without a close/reopen. Works for create, rename, and edit.
        setWarning(null);
        setTagGenPhase('generating');
        startTagGenPolling(savedName, countItemTags(resumeToSave), JSON.stringify(resumeToSave));
      } else if (resumename && savedName === resumename) {
        // Keep-open save in edit mode with no rename and nothing to generate:
        // reload in place so any other server-side changes appear. (Create and
        // rename cases reload via the prop-change load effect.)
        await loadResume();
      }
      if (!closeOnSuccess) {
        // Re-arm the "saved" baseline AFTER any in-place reload, so the footer
        // reports the state that is actually on screen. (When closing, the
        // isOpen effect resets the baseline instead.)
        baselineArmedRef.current = false;
        setBaselineNonce(n => n + 1);
      }
    } catch (err: any) {
      // Show specific error message if available
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to save resume');
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // Scorecard inline item edit: apply the new text to the in-memory resume and
  // persist immediately. Throws on API failure so the scorecard row can show an
  // inline error (handleSave swallows errors into the editor banner, which is
  // hidden behind the scorecard modal — so saved resumes call the API directly).
  async function handleScorecardItemSave(path: FindingPath, newText: string) {
    const updated = setTextAtPath(resume, path, newText);
    setResume(updated);
    if (resumename) {
      await api.updateResume(resumename, withSectionSettings(updated), undefined, masterResumePath, publishMasterResume);
      onSuccess(resumename);
    } else {
      // Brand-new unsaved resume: route through handleSave so create-mode
      // validation (resume name required, etc.) applies.
      await handleSave(false, updated);
    }
  }

  // Scorecard "Mark resolved" — persist dismissed finding ids into resume state
  // (in resumejson.scorecard_resolved). Saved with the resume on the next Save,
  // mirroring how recommendations persist — no per-click API call / tag churn.
  function handleScorecardResolvedChange(resolvedIds: string[]) {
    setResume((prev) => ({ ...prev, scorecard_resolved: resolvedIds }));
  }

  // AI Review functions
  const allRecs = resume.recommendations || [];
  const hasReviewedBefore = allRecs.length > 0;
  const pendingRecs = allRecs.filter(r => r.status === 'pending');
  const hasRecommendations = pendingRecs.length > 0;
  // "All recommendations have been implemented" green banner: only once — after
  // a review where every recommendation is now resolved, and not yet acked.
  const showAllImplemented = hasReviewedBefore && !hasRecommendations && !recsImplementedAcked;

  // Open recommendations per NAV ROW — drives the rail's amber "needs
  // attention" dots and its "N need attention" summary line.
  const navAttentionCounts = useMemo(() => {
    const byKey: Record<string, number> = {};
    for (const rec of pendingRecs) {
      const root = rec.path.split('[')[0].split('.')[0];
      const key = root === 'name' ? 'basics' : root;
      byKey[key] = (byKey[key] || 0) + 1;
    }
    return byKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume.recommendations]);

  // Rail footer chip → the pending-recommendations banner in the editor pane.
  function scrollToRecommendations() {
    const root = scrollRef.current;
    const el = root?.querySelector<HTMLElement>('[data-review-anchor]');
    if (!root || !el) return;
    const top = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 8;
    root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  // Per-section recommendation counts for top summary
  function getRecSectionCounts(): { section: string; count: number }[] {
    const counts: Record<string, number> = {};
    for (const rec of pendingRecs) {
      const section = rec.path.split('[')[0].split('.')[0];
      counts[section] = (counts[section] || 0) + 1;
    }
    return Object.entries(counts).map(([section, count]) => ({ section, count }));
  }

  async function handleAIReview() {
    if (!resumename) {
      setError('Save the resume first before running AI Review');
      return;
    }
    // Gate AI features for restricted user statuses
    try {
      const prefs = await api.getPreferences();
      const restrictedStatuses = ['trial', 'hibernating', 'cancelled', 'expired'];
      if (prefs.status && restrictedStatuses.includes(prefs.status)) {
        setShowAiUpgradeModal(true);
        return;
      }
    } catch {
      // If we can't check, allow the request (backend will enforce)
    }
    setReviewing(true);
    setError(null);
    try {
      const result = await api.reviewResume(resumename);
      const newRecs = result.recommendations || [];
      // Merge: keep existing dismissed/accepted, add new pending ones
      const existing = resume.recommendations || [];
      const kept = existing.filter(r => r.status === 'dismissed' || r.status === 'accepted');
      setResume({ ...resume, recommendations: [...kept, ...newRecs] });
      setReviewComplete(true);
      setEditedReplacements({});
      // Fresh review re-arms the "all implemented" banner so it can show once
      // more after this round of recommendations is fully applied.
      setRecsImplementedAcked(false);
      try { if (recsAckKey) localStorage.removeItem(recsAckKey); } catch { /* ignore */ }
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to generate AI review. Please try again.');
      }
      console.error(err);
    } finally {
      setReviewing(false);
    }
  }

  function itemLabel(item: unknown): string {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      const obj = item as Record<string, unknown>;
      const candidate = obj.title ?? obj.text ?? obj.name;
      if (typeof candidate === 'string') return candidate;
      return JSON.stringify(item);
    }
    return String(item);
  }

  function getDataDisplay(rec: Recommendation): string {
    const norm = normalizeRec(rec);
    const val = norm.data;
    if (val == null) return '';
    if (Array.isArray(val)) return val.map(itemLabel).join(', ');
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return itemLabel(val);
    return JSON.stringify(val);
  }

  function buildDataFromEdit(rec: Recommendation, editedText: string): unknown {
    const norm = normalizeRec(rec);
    const originalVal = norm.data;
    if (Array.isArray(originalVal)) {
      const labels = editedText.split(',').map(s => s.trim()).filter(Boolean);
      // If original items were objects, preserve their structure and update the label field.
      const first = originalVal.length > 0 ? originalVal[0] : null;
      if (first != null && typeof first === 'object') {
        const firstObj = first as Record<string, unknown>;
        const labelField = 'title' in firstObj ? 'title' : 'text' in firstObj ? 'text' : 'name';
        return labels.map((label, i) => {
          const orig = originalVal[i];
          if (orig != null && typeof orig === 'object') {
            return { ...(orig as Record<string, unknown>), [labelField]: label };
          }
          return { [labelField]: label, tags: [] };
        });
      }
      return labels;
    }
    return editedText;
  }

  function applyRecommendation(rec: Recommendation) {
    const norm = normalizeRec(rec);
    if (norm.action === 'advise') return;   // informational: nothing to apply
    const editedText = editedReplacements[rec.id];
    const data = editedText !== undefined ? buildDataFromEdit(rec, editedText) : norm.data;
    const updated = applyActionAtPath(resume, norm.path, norm.field, norm.action, data);
    const updatedRecs = (updated.recommendations || []).map(r =>
      r.id === rec.id ? { ...r, status: 'accepted' as const } : r
    );
    setResume({ ...updated, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[rec.id]; return next; });
  }

  function dismissRecommendation(recId: string) {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.id === recId ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
    setEditedReplacements(prev => { const next = {...prev}; delete next[recId]; return next; });
  }

  function renderInlineRecs(path: string) {
    const matching = pendingRecs.filter(r => r.path === path);
    if (matching.length === 0) return null;
    const actionColors: Record<string, string> = {
      replace: 'bg-blue-100 text-blue-700',
      append: 'bg-emerald-100 text-emerald-700',
      remove: 'bg-red-100 text-red-700',
    };
    return (
      <>
        {matching.map(rec => {
          const norm = normalizeRec(rec);
          // 'advise' = a note with no safe mechanical fix: no editable value,
          // no Apply — the user reads it and dismisses it.
          if (norm.action === 'advise') {
            return (
              <div key={rec.id} className="ml-6 mt-0.5 mb-1 relative">
                <div className="absolute -top-1 left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-amber-200" />
                <div className="border border-amber-200 bg-amber-50 rounded pl-3 pr-2 py-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="inline-block px-1.5 py-0 text-[10px] font-medium rounded bg-amber-100 text-amber-800">
                          note
                        </span>
                        <p className="text-xs text-amber-800">{rec.desc}</p>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => dismissRecommendation(rec.id)}
                          className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          <XCircle className="w-3 h-3" /> Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          const displayVal = editedReplacements[rec.id] ?? getDataDisplay(rec);
          const isMultiline = rec.field === 'text' || rec.field === 'summary' || displayVal.length > 80;
          const isRemove = norm.action === 'remove';
          return (
            <div key={rec.id} className="ml-6 mt-0.5 mb-1 relative">
              {/* Arrow pointing up to the element above */}
              <div className="absolute -top-1 left-4 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-purple-200" />
              <div className="border border-purple-200 bg-purple-50 rounded pl-3 pr-2 py-2">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-purple-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`inline-block px-1.5 py-0 text-[10px] font-medium rounded ${actionColors[norm.action] || actionColors.replace}`}>
                        {norm.action}
                      </span>
                      <p className="text-xs text-purple-700">{rec.desc}</p>
                    </div>
                    {isRemove ? (
                      <p className="text-xs text-red-600 line-through px-2 py-1">{displayVal}</p>
                    ) : isMultiline ? (
                      <textarea
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        rows={rec.field === 'summary' || rec.path === 'summary' ? 5 : 2}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white resize-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={displayVal}
                        onChange={e => setEditedReplacements(prev => ({...prev, [rec.id]: e.target.value}))}
                        className="w-full px-2 py-1 border border-purple-200 rounded text-xs bg-white"
                      />
                    )}
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={() => applyRecommendation(rec)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                      >
                        <Check className="w-3 h-3" /> Apply
                      </button>
                      <button
                        onClick={() => dismissRecommendation(rec.id)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        <XCircle className="w-3 h-3" /> Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    );
  }

  function applyAllRecommendations() {
    let updated = { ...resume };
    const recs = updated.recommendations || [];
    for (const rec of recs) {
      if (rec.status === 'pending') {
        const norm = normalizeRec(rec);
        if (norm.action === 'advise') continue;   // notes are never applied
        updated = applyActionAtPath(updated, norm.path, norm.field, norm.action, norm.data);
      }
    }
    // 'advise' notes stay pending — Apply All must not silently accept them.
    updated.recommendations = recs.map(r =>
      r.status === 'pending' && normalizeRec(r).action !== 'advise'
        ? { ...r, status: 'accepted' as const } : r
    );
    setResume(updated);
  }

  function dismissAllRecommendations() {
    const updatedRecs = (resume.recommendations || []).map(r =>
      r.status === 'pending' ? { ...r, status: 'dismissed' as const } : r
    );
    setResume({ ...resume, recommendations: updatedRecs });
  }

  // Skills functions

  function updateSkill(index: number, value: string) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], title: value };
    setResume({ ...resume, skills });
  }

  function updateSkillTags(index: number, tags: string[]) {
    const skills = [...resume.skills];
    skills[index] = { ...skills[index], tags };
    setResume({ ...resume, skills });
  }

  function removeSkill(index: number) {
    setResume({ ...resume, skills: resume.skills.filter((_, i) => i !== index) });
  }

  // ── Skill Groups (task 9) ──────────────────────────────────────────────
  // Groups are user-defined buckets; each skill carries an optional group_id.
  // Skills with no/unknown group_id render in the "Ungrouped" lane. The server
  // auto-buckets ungrouped skills on save; explicit placement here always wins.
  function genGroupId(): string {
    const hex = Array.from({ length: 8 }, () =>
      Math.floor(Math.random() * 16).toString(16)).join('');
    return `sg_${hex}`;
  }

  function addSkillGroup() {
    const groups = resume.skill_groups || [];
    const maxOrder = groups.reduce((m, g) => Math.max(m, g.order ?? 0), -1);
    const ng = { id: genGroupId(), title: '', tags: [], order: maxOrder + 1 };
    setResume({ ...resume, skill_groups: [...groups, ng] });
    setEditingGroupId(ng.id);
  }

  function updateSkillGroupTitle(id: string, title: string) {
    setResume({
      ...resume,
      skill_groups: (resume.skill_groups || []).map(g => g.id === id ? { ...g, title } : g),
    });
  }

  function removeSkillGroup(id: string) {
    // Drop the group; its skills fall back to ungrouped (group_id cleared).
    setResume({
      ...resume,
      skill_groups: (resume.skill_groups || []).filter(g => g.id !== id),
      skills: resume.skills.map(s => s.group_id === id ? { ...s, group_id: null } : s),
    });
  }

  function addSkillToGroup(groupId: string) {
    const key = groupId || '';
    const val = (newSkillByGroup[key] || '').trim();
    if (!val) return;
    setResume({
      ...resume,
      skills: [...resume.skills, { title: val, tags: [], group_id: groupId || null }],
    });
    setNewSkillByGroup(prev => ({ ...prev, [key]: '' }));
  }

  function moveSkillToGroup(skillIdx: number, groupId: string | null) {
    if (skillIdx == null || skillIdx < 0 || skillIdx >= resume.skills.length) return;
    const skills = [...resume.skills];
    if ((skills[skillIdx].group_id ?? null) === (groupId ?? null)) return;
    skills[skillIdx] = { ...skills[skillIdx], group_id: groupId };
    setResume({ ...resume, skills });
  }

  // ── Synonym/skill conflict detection (task 8) ──────────────────────────
  // Mirrors shared/synonyms.py: a skill "belongs to" a set when its title
  // contains a member phrase (prefix-subset rule). When 2+ skills map to the
  // SAME set, a job-time synonym swap would rewrite both to the winner and
  // duplicate the skill — so we block the save and flag both sides.
  function synTokenize(text: string): string[] {
    return (text || '').toLowerCase().match(/[a-z0-9+#&.']+/g) || [];
  }
  function synSeqMatch(memberTokens: string[], textTokens: string[]): boolean {
    const m = memberTokens.length;
    if (m === 0) return false;
    for (let i = 0; i <= textTokens.length - m; i++) {
      if (memberTokens.every((mt, k) => textTokens[i + k].startsWith(mt))) return true;
    }
    return false;
  }
  function computeSynonymConflicts(rj: ResumeJSON): { skillIdxs: Set<number>; setIds: Set<string> } {
    const skillIdxs = new Set<number>();
    const setIds = new Set<string>();
    const sets = (rj.synonyms || []).filter(s => (s.terms || []).filter(t => t.trim()).length >= 2);
    const skillTokenLists = rj.skills.map(s => synTokenize(s.title || ''));
    for (const set of sets) {
      const memberTokenLists = (set.terms || []).map(t => synTokenize(t)).filter(t => t.length);
      const matching: number[] = [];
      for (let i = 0; i < skillTokenLists.length; i++) {
        if (memberTokenLists.some(mt => synSeqMatch(mt, skillTokenLists[i]))) matching.push(i);
      }
      if (matching.length >= 2) {
        matching.forEach(i => skillIdxs.add(i));
        setIds.add(set.id);
      }
    }
    return { skillIdxs, setIds };
  }

  // Per-item tag status chip, shown OUTSIDE the tags box so it's visible even
  // when the tag set already has (too few) entries. Rendered whenever an
  // item's tags are below the per-item minimum the backend targets — exactly
  // the items the next save (or the run currently in flight) will (re)tag.
  // Items at/above the minimum keep their tags on save, so no chip = no
  // regeneration.
  function renderTagStatusChip(tags: string[] | undefined, text: string | undefined, min: number = TAG_MIN_ITEM) {
    if (!(text || '').trim() || (tags?.length || 0) >= min) return null;
    const generating = tagGenPhase === 'generating';
    return (
      <span
        data-testid="tag-status-chip"
        className={`inline-flex items-center gap-1 mt-0.5 px-1.5 py-px rounded-full text-[10px] font-medium whitespace-nowrap border ${
          generating
            ? 'bg-amber-50 text-amber-700 border-amber-200'
            : 'bg-gray-50 text-gray-500 border-gray-200'
        }`}
        title={generating
          ? 'Tags for this item are being generated now — they\'ll appear here automatically.'
          : `This item has fewer than ${min} tags, so tags will be (re)generated the next time you save. Items with ${min}+ tags keep their tags — clear the tags box to force a refresh.`}
      >
        {generating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Clock className="w-2.5 h-2.5" />}
        {generating ? 'generating tags…' : 'tags generate on Save'}
      </span>
    );
  }

  // One draggable skill chip (used by every group lane). `i` is the skill's
  // index in the flat resume.skills array — all mutators key off it.
  function renderSkillCard(skill: any, i: number) {
    const conflict = conflictSkillIdxs.has(i);
    return (
      <div
        key={i}
        data-skill-index={i}
        draggable={editingSkillIndex !== i}
        onDragStart={(e) => {
          setDraggedSkillIdx(i);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(i));
        }}
        onDragEnd={() => { setDraggedSkillIdx(null); setDragOverGroup(null); }}
        className={`flex items-start gap-1 px-2 py-1.5 rounded-lg text-sm min-w-[140px] max-w-[220px] ${editingSkillIndex === i ? '' : 'cursor-grab active:cursor-grabbing'} ${conflict ? 'bg-red-50 ring-2 ring-red-400' : 'bg-gray-100'}${skill.include === '0' ? ' opacity-40' : ''}`}
        title={conflict ? 'This skill clashes with a synonym set — see the warning above.' : undefined}
      >
        <input
          type="checkbox"
          checked={skill.include !== '0'}
          onChange={(e) => {
            const skills = [...resume.skills];
            skills[i] = { ...skills[i], include: e.target.checked ? '1' : '0' };
            setResume({ ...resume, skills });
          }}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-0.5 shrink-0 w-3 h-3"
          title={skill.include === '0' ? 'Excluded from generation' : 'Included in generation'}
        />
        <div className="flex-1 min-w-0">
          {editingSkillIndex === i ? (
            <>
              <input type="text" value={skill.title} onChange={(e) => updateSkill(i, e.target.value)} onKeyDown={(e) => e.key === 'Enter' && setEditingSkillIndex(null)} className="w-full px-1 py-0 border border-gray-300 rounded text-sm mb-1" autoFocus autoComplete="off" />
              <input
                key={(skill.tags || []).join(',')}
                type="text"
                defaultValue={(skill.tags || []).join(', ')}
                onBlur={(e) => updateSkillTags(i, e.target.value.split(',').map((t: string) => t.trim()).filter(Boolean))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateSkillTags(i, e.currentTarget.value.split(',').map((t: string) => t.trim()).filter(Boolean));
                    setEditingSkillIndex(null);
                  }
                }}
                className="w-full px-1 py-0 border border-gray-100 rounded text-xs text-gray-500"
                placeholder="tags (comma-separated)"
              />
              {renderTagStatusChip(skill.tags, skill.title)}
            </>
          ) : (
            <div onClick={() => setEditingSkillIndex(i)} className="cursor-pointer">
              <div className="truncate leading-tight">{skill.title}</div>
              {(skill.tags || []).length > 0 && (
                <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{skill.tags.join(', ')}</div>
              )}
              {renderTagStatusChip(skill.tags, skill.title)}
            </div>
          )}
        </div>
        <button onClick={() => { if (editingSkillIndex === i) setEditingSkillIndex(null); removeSkill(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-0.5">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // A per-group "add skill" input (compact). key '' = ungrouped lane.
  function renderGroupAddInput(groupId: string) {
    const key = groupId || '';
    return (
      <div className="flex gap-1 mt-1.5">
        <input
          type="text"
          value={newSkillByGroup[key] || ''}
          onChange={(e) => setNewSkillByGroup(prev => ({ ...prev, [key]: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && addSkillToGroup(groupId)}
          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
          placeholder="+ add skill…"
          autoComplete="off"
        />
        <button onClick={() => addSkillToGroup(groupId)} className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200" title="Add skill to this group">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  // Summary Phrase functions
  function addSummaryPhrase() {
    if (newSummaryPhrase.trim()) {
      setResume({ ...resume, summary_phrases: [...(resume.summary_phrases || []), { text: newSummaryPhrase.trim(), tags: [] }] });
      setNewSummaryPhrase('');
    }
  }

  function updateSummaryPhraseText(index: number, value: string) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], text: value };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function updateSummaryPhraseTags(index: number, tags: string[]) {
    const phrases = [...(resume.summary_phrases || [])];
    phrases[index] = { ...phrases[index], tags };
    setResume({ ...resume, summary_phrases: phrases });
  }

  function removeSummaryPhrase(index: number) {
    setResume({ ...resume, summary_phrases: (resume.summary_phrases || []).filter((_, i) => i !== index) });
  }

  // Contact functions
  function addContactItem() {
    if (newContactTitle.trim()) {
      const newItem: ContactItem = {
        icon: newContactIcon,
        title: newContactTitle.trim(),
        url: newContactUrl.trim(),
      };
      setResume({
        ...resume,
        contact: [...resume.contact, newItem]
      });
      setNewContactTitle('');
      setNewContactUrl('');
      setNewContactAutoFill(false);
    }
  }

  function updateContactItem(index: number, updates: Partial<ContactItem>) {
    const items = [...resume.contact];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, contact: items });
  }

  function removeContactItem(index: number) {
    setResume({
      ...resume,
      contact: resume.contact.filter((_, i) => i !== index)
    });
  }

  function moveContactItem(index: number, direction: 'up' | 'down') {
    const items = [...resume.contact];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, contact: items });
  }

  // Synonym set functions — terms are entered as one line separated by
  // "//" (or commas); each set gets a stable PK so backend processes can
  // reference it deterministically.
  function parseSynonymTerms(raw: string): string[] {
    const seen = new Set<string>();
    return raw
      .split(/\/\/|,/)
      .map(t => t.trim())
      .filter(t => {
        if (!t) return false;
        const key = t.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  function addSynonymSet() {
    const terms = parseSynonymTerms(newSynonymTerms);
    if (terms.length < 2) return;
    const newSet: SynonymSet = {
      id: `syn_${Math.random().toString(36).slice(2, 10)}`,
      terms,
    };
    setResume({ ...resume, synonyms: [...(resume.synonyms || []), newSet] });
    setNewSynonymTerms('');
  }

  function commitSynonymDraft(id: string) {
    const draft = synonymDrafts[id];
    if (draft === undefined) return;
    const terms = parseSynonymTerms(draft);
    setResume({
      ...resume,
      synonyms: (resume.synonyms || []).map(s => s.id === id ? { ...s, terms } : s),
    });
    setSynonymDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  function removeSynonymSet(id: string) {
    setResume({
      ...resume,
      synonyms: (resume.synonyms || []).filter(s => s.id !== id),
    });
    setSynonymDrafts(prev => { const next = { ...prev }; delete next[id]; return next; });
  }

  // Education functions
  function addEducation() {
    if (newEduTitle.trim() && newEduOrg.trim()) {
      const newEdu: Education = {
        title: newEduTitle.trim(),
        org: newEduOrg.trim(),
        year: newEduYear.trim(),
        tags: [],
      };
      setResume({ ...resume, education: [...resume.education, newEdu] });
      setNewEduTitle('');
      setNewEduOrg('');
      setNewEduYear('');
    }
  }

  function updateEducation(index: number, updates: Partial<Education>) {
    const items = [...resume.education];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, education: items });
  }

  function removeEducation(index: number) {
    setResume({ ...resume, education: resume.education.filter((_, i) => i !== index) });
  }

  function moveEducation(index: number, direction: 'up' | 'down') {
    const items = [...resume.education];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, education: items });
  }

  // Award functions
  function addAward() {
    if (newAwardTitle.trim()) {
      const newAwardItem: Award = {
        title: newAwardTitle.trim(),
        issuer: newAwardIssuer.trim(),
        reward: newAwardReward.trim(),
        year: newAwardYear.trim(),
        tags: [],
      };
      setResume({ ...resume, awards: [...resume.awards, newAwardItem] });
      setNewAwardTitle('');
      setNewAwardIssuer('');
      setNewAwardReward('');
      setNewAwardYear('');
    }
  }

  function updateAward(index: number, updates: Partial<Award>) {
    const items = [...resume.awards];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, awards: items });
  }

  function removeAward(index: number) {
    setResume({ ...resume, awards: resume.awards.filter((_, i) => i !== index) });
  }

  function moveAward(index: number, direction: 'up' | 'down') {
    const items = [...resume.awards];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, awards: items });
  }

  // Presentation functions
  function addPresentation() {
    if (newPresentationTitle.trim()) {
      const newPresentationItem: Presentation = {
        title: newPresentationTitle.trim(),
        event: newPresentationEvent.trim(),
        year: newPresentationYear.trim(),
        tags: [],
      };
      setResume({ ...resume, presentations: [...(resume.presentations || []), newPresentationItem] });
      setNewPresentationTitle('');
      setNewPresentationEvent('');
      setNewPresentationYear('');
    }
  }

  function updatePresentation(index: number, updates: Partial<Presentation>) {
    const items = [...(resume.presentations || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, presentations: items });
  }

  function removePresentation(index: number) {
    setResume({ ...resume, presentations: (resume.presentations || []).filter((_, i) => i !== index) });
  }

  function movePresentation(index: number, direction: 'up' | 'down') {
    const items = [...(resume.presentations || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, presentations: items });
  }

  // Publication functions
  function addPublication() {
    if (newPubTitle.trim()) {
      const newPubItem: Publication = {
        title: newPubTitle.trim(),
        url: newPubUrl.trim() || undefined,
        year: newPubYear.trim(),
        tags: [],
      };
      setResume({ ...resume, publications: [...(resume.publications || []), newPubItem] });
      setNewPubTitle('');
      setNewPubUrl('');
      setNewPubYear('');
    }
  }

  function updatePublication(index: number, updates: Partial<Publication>) {
    const items = [...(resume.publications || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, publications: items });
  }

  function removePublication(index: number) {
    setResume({ ...resume, publications: (resume.publications || []).filter((_, i) => i !== index) });
  }

  function movePublication(index: number, direction: 'up' | 'down') {
    const items = [...(resume.publications || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, publications: items });
  }

  // Portfolio functions
  function addPortfolio() {
    if (newPortTitle.trim()) {
      const newPortItem: Portfolio = {
        title: newPortTitle.trim(),
        url: newPortUrl.trim() || undefined,
        year: newPortYear.trim(),
        tags: [],
      };
      setResume({ ...resume, portfolios: [...(resume.portfolios || []), newPortItem] });
      setNewPortTitle('');
      setNewPortUrl('');
      setNewPortYear('');
    }
  }

  function updatePortfolio(index: number, updates: Partial<Portfolio>) {
    const items = [...(resume.portfolios || [])];
    items[index] = { ...items[index], ...updates };
    setResume({ ...resume, portfolios: items });
  }

  function removePortfolio(index: number) {
    setResume({ ...resume, portfolios: (resume.portfolios || []).filter((_, i) => i !== index) });
  }

  function movePortfolio(index: number, direction: 'up' | 'down') {
    const items = [...(resume.portfolios || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setResume({ ...resume, portfolios: items });
  }

  // Demographics functions (optional, extension-only — never on the resume)
  function updateDemographics(updates: Partial<Demographics>) {
    setResume({
      ...resume,
      demographics: { ...emptyDemographics, ...(resume.demographics || {}), ...updates },
    });
  }

  // Experience functions
  function getExperience(): ExperienceCompany[] {
    return (resume.experience || []) as ExperienceCompany[];
  }

  function setExperience(exp: ExperienceCompany[]) {
    setResume({ ...resume, experience: exp });
  }

  function addCompany() {
    const newCompany: ExperienceCompany = {
      company: '',
      url: [],
      startDate: '',
      current: false,
      roles: []
    };
    setExperience([...getExperience(), newCompany]);
    setEditingCompanyIndex(getExperience().length);
  }

  function updateCompany(index: number, updates: Partial<ExperienceCompany>) {
    const exp = [...getExperience()];
    exp[index] = { ...exp[index], ...updates };
    setExperience(exp);
  }

  function removeCompany(index: number) {
    setExperience(getExperience().filter((_, i) => i !== index));
    setEditingCompanyIndex(null);
  }

  function moveCompany(index: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= exp.length) return;
    [exp[index], exp[newIndex]] = [exp[newIndex], exp[index]];
    setExperience(exp);
  }

  function addRole(companyIndex: number) {
    const exp = [...getExperience()];
    const newRole: ExperienceRole = {
      title: '',
      startDate: '',
      current: false,
      bullets: []
    };
    exp[companyIndex].roles = [...exp[companyIndex].roles, newRole];
    setExperience(exp);
    setEditingRoleIndex({ companyIdx: companyIndex, roleIdx: exp[companyIndex].roles.length - 1 });
  }

  function updateRole(companyIndex: number, roleIndex: number, updates: Partial<ExperienceRole>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex] = { ...exp[companyIndex].roles[roleIndex], ...updates };
    setExperience(exp);
  }

  function removeRole(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles = exp[companyIndex].roles.filter((_, i) => i !== roleIndex);
    setExperience(exp);
    setEditingRoleIndex(null);
  }

  function moveRole(companyIndex: number, roleIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const roles = [...exp[companyIndex].roles];
    const newIndex = direction === 'up' ? roleIndex - 1 : roleIndex + 1;
    if (newIndex < 0 || newIndex >= roles.length) return;
    [roles[roleIndex], roles[newIndex]] = [roles[newIndex], roles[roleIndex]];
    exp[companyIndex].roles = roles;
    setExperience(exp);
  }

  function addBullet(companyIndex: number, roleIndex: number) {
    const exp = [...getExperience()];
    const newBullet: ExperienceBullet = { text: '', tags: [] };
    exp[companyIndex].roles[roleIndex].bullets = [...exp[companyIndex].roles[roleIndex].bullets, newBullet];
    setExperience(exp);
  }

  function updateBullet(companyIndex: number, roleIndex: number, bulletIndex: number, updates: Partial<ExperienceBullet>) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
      ...exp[companyIndex].roles[roleIndex].bullets[bulletIndex],
      ...updates
    };
    setExperience(exp);
  }

  function removeBullet(companyIndex: number, roleIndex: number, bulletIndex: number) {
    const exp = [...getExperience()];
    exp[companyIndex].roles[roleIndex].bullets = exp[companyIndex].roles[roleIndex].bullets.filter((_, i) => i !== bulletIndex);
    setExperience(exp);
  }

  function moveBullet(companyIndex: number, roleIndex: number, bulletIndex: number, direction: 'up' | 'down') {
    const exp = [...getExperience()];
    const bullets = [...exp[companyIndex].roles[roleIndex].bullets];
    const newIndex = direction === 'up' ? bulletIndex - 1 : bulletIndex + 1;
    if (newIndex < 0 || newIndex >= bullets.length) return;
    [bullets[bulletIndex], bullets[newIndex]] = [bullets[newIndex], bullets[bulletIndex]];
    exp[companyIndex].roles[roleIndex].bullets = bullets;
    setExperience(exp);
  }

  function getIconSvg(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.svg || '/icons/globe.svg';
  }

  function getUrlPlaceholder(iconName: ContactItem['icon']) {
    const iconConfig = iconOptions.find(i => i.value === iconName);
    return iconConfig?.placeholder || 'https://example.com';
  }

  function autoFillContactUrl(icon: ContactItem['icon'], title: string): string | null {
    const trimmed = title.trim();
    if (!trimmed) return null;
    const contactPrefix: Record<string, string> = {
      'email-at': 'mailto:',
      'phone': 'tel:+',
      'phone-volume': 'tel:+',
      'linkedin': 'https://www.linkedin.com/in/',
      'x-twitter': 'https://x.com/',
      'github': 'https://github.com/',
      'github-square': 'https://github.com/',
      'discord': 'https://discord.com/users/',
      'telegram': 'https://t.me/',
      'signal-chat': 'https://signal.me/#u/',
    };
    const prefix = contactPrefix[icon];
    if (!prefix) return null;
    if (icon === 'phone' || icon === 'phone-volume') {
      const digits = trimmed.replace(/\D/g, '');
      if (!digits) return null;
      const normalized = (digits.length === 10 && /^[2-9]/.test(digits)) ? `1${digits}` : digits;
      return `tel:+${normalized}`;
    }
    if (icon === 'email-at') {
      return `mailto:${trimmed}`;
    }
    return `${prefix}${trimmed}`;
  }

  // Import functions
  function handleLoadFromFileClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be selected again
    event.target.value = '';

    setImporting(true);
    setError(null);
    setWarning(null);
    setTagGenPhase('idle');
    if (tagPollRef.current) tagPollRef.current.cancelled = true;

    try {
      const result = await importResumeFile(file);

      // Populate form with imported data
      setResume(result.resumejson);

      // Build the friendly import-notes message (shown only when we are NOT
      // about to kick off tag generation; otherwise the tag-gen status box
      // replaces it).
      let importNotes: string | null = null;
      if (result.warnings && result.warnings.length > 0) {
        const friendly = result.warnings.map(w => {
          // Rephrase Schema warnings into plain language
          const schemaMatch = w.match(/^Schema: (\w+)\[0\]: missing required field '(\w+)'$/);
          if (schemaMatch) return `Did not find a ${schemaMatch[1]} section`;
          if (w.includes('processed by AI')) return 'PDF content was processed by AI — please verify accuracy';
          return w;
        });
        importNotes = `Import completed successfully, with a few notes:\n${friendly.map(f => `• ${f}`).join('\n')}`;
      }

      // R7: kick off tag generation immediately by auto-saving (without closing).
      // Pass the freshly imported data explicitly so we don't depend on the
      // setResume state commit above. The async tag pipeline picks up from the
      // save. A brand-new resume normally has an empty name field, which used to
      // silently skip the auto-save — so tags never generated and no status
      // showed. When this is a NEW resume with no name, default it to a unique
      // name based on the imported candidate name so generation always runs.
      const candidateName = (result.resumejson?.name || '').trim();
      let effectiveName = name.trim();
      if (!effectiveName && !resumename && candidateName) {
        effectiveName = await deriveUniqueResumeName(candidateName);
      }
      // Auto-save kicks off tag generation. handleSave shows the tag-gen status
      // and polls until tags land (see its keep-open branch), so the imported
      // resume's tags appear without a manual close/reopen.
      const willSave = !!(effectiveName && result.resumejson?.name);
      if (willSave) {
        if (!name.trim()) setName(effectiveName);
        handleSave(false, result.resumejson, effectiveName).catch((e) => {
          console.warn('Auto-save after import failed:', e);
          if (importNotes) setWarning(importNotes);
        });
      } else if (importNotes) {
        // Could not auto-save (e.g. the importer didn't extract a name) — show
        // the import notes so the user knows to review and save manually.
        setWarning(importNotes);
      }
    } catch (err) {
      // Preserve existing form data on error (Requirement 6.6)
      // Handle specific error types with user-friendly messages (Requirements 8.1-8.5)
      if (err instanceof ImportValidationError) {
        setError(err.message);
      } else if (err instanceof ImportUploadError) {
        setError(err.message);
      } else if (err instanceof ImportProcessError) {
        setError(err.message);
      } else if (err instanceof ImportAuthError) {
        setError(err.message);
      } else if (err instanceof ImportTimeoutError) {
        setError(err.message);
      } else {
        // Generic fallback error (Requirement 8.4)
        setError('Could not read file. Please ensure it\'s a valid YAML, JSON, or PDF file.');
        // Log detailed error for debugging (Requirement 8.5)
        console.error('Import error:', err);
      }
    } finally {
      setImporting(false);
    }
  }

  function handleDownloadTemplate() {
    // Check if resume has real data (not just empty/default values)
    const hasRealData = resume.name ||
                        resume.skills.length > 0 ||
                        resume.summary ||
                        getExperience().length > 0 ||
                        resume.education.length > 0 ||
                        resume.awards.length > 0 ||
                        (resume.presentations || []).length > 0 ||
                        (resume.publications || []).length > 0 ||
                        (resume.portfolios || []).length > 0 ||
                        resume.contact.length > 0;

    if (!hasRealData) {
      // No real data - download the template
      const link = document.createElement('a');
      link.href = '/skillsnap-resume-template.yaml';
      link.download = 'skillsnap-resume-template.yaml';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Convert resume data to YAML format
    const yamlData: Record<string, unknown> = {
      name: resume.name || 'Your Full Name',
    };

    // Add summary
    if (resume.summary) {
      yamlData.summary = resume.summary;
    }

    // Convert contacts (unified format: title/icon/url)
    if (resume.contact.length > 0) {
      yamlData.contacts = resume.contact.map(item => ({
          title: item.title,
          icon: item.icon,
          url: item.url,
        }));
    }

    // Add skills (include tags for AI matching)
    if (resume.skills.length > 0) {
      yamlData.skills = resume.skills.map(s => ({
        title: s.title,
        tags: s.tags || []
      }));
    }

    // Convert experience (canonical format: startDate/endDate/current/title)
    if (getExperience().length > 0) {
      yamlData.experience = getExperience().map(company => {
        const expEntry: Record<string, unknown> = {
          company: company.company,
        };
        if (company.url && company.url.length > 0) expEntry.url = company.url;
        if (company.employees) expEntry.employees = company.employees;
        expEntry.startDate = company.startDate || '';
        expEntry.endDate = company.endDate || '';
        expEntry.current = company.current || false;

        if (company.location) expEntry.location = company.location;
        if (company.description) expEntry.description = company.description;

        // Convert roles
        expEntry.roles = company.roles.map(role => {
          const roleEntry: Record<string, unknown> = {
            title: role.title,
            startDate: role.startDate || '',
            endDate: role.endDate || '',
            current: role.current || false,
          };

          if (role.location) roleEntry.location = role.location;

          roleEntry.bullets = role.bullets.map(bullet => ({
            text: bullet.text,
            tags: bullet.tags || []
          }));

          return roleEntry;
        });

        return expEntry;
      });
    }

    // Convert education (canonical field names + tags)
    if (resume.education.length > 0) {
      yamlData.education = resume.education.map(edu => ({
        title: edu.title,
        org: edu.org,
        url: edu.url || '',
        year: edu.year || '',
        tags: edu.tags || []
      }));
    }

    // Convert awards (canonical field names + tags)
    if (resume.awards.length > 0) {
      yamlData.awards = resume.awards.map(award => ({
        title: award.title,
        issuer: award.issuer || '',
        reward: award.reward || '',
        url: award.url || '',
        year: award.year || '',
        tags: award.tags || []
      }));
    }

    // Convert presentations (add tags)
    if ((resume.presentations || []).length > 0) {
      yamlData.presentations = (resume.presentations || []).map(presentation => ({
        title: presentation.title,
        event: presentation.event || '',
        url: presentation.url || '',
        year: presentation.year || '',
        tags: presentation.tags || []
      }));
    }

    // Convert publications (add tags)
    if ((resume.publications || []).length > 0) {
      yamlData.publications = (resume.publications || []).map(pub => ({
        title: pub.title,
        url: pub.url || '',
        year: pub.year || '',
        tags: pub.tags || []
      }));
    }

    // Convert portfolios (add tags)
    if ((resume.portfolios || []).length > 0) {
      yamlData.portfolios = (resume.portfolios || []).map(port => ({
        title: port.title,
        url: port.url || '',
        year: port.year || '',
        tags: port.tags || []
      }));
    }

    // Convert to YAML string with header comment
    const yamlHeader = `# SkillSnap Resume Export
# Generated from: ${name || 'Untitled Resume'}
# Date: ${new Date().toISOString().split('T')[0]}
# Import this file using "Load from File" in SkillSnap.

`;
    const yamlContent = yamlHeader + yaml.dump(yamlData, {
      indent: 2,
      lineWidth: -1, // Don't wrap lines
      noRefs: true,
      quotingType: '"',
      forceQuotes: false
    });

    // Create and download the file
    const blob = new Blob([yamlContent], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = name ? `${name.replace(/[^a-zA-Z0-9]/g, '_')}.yaml` : 'resume.yaml';
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Export the full, unfiltered resume (all sections / all items, no page-fit
  // pruning) as a PDF or plain-text file, then open it in a new tab.
  async function handleExportFull(format: 'pdf' | 'text') {
    setExporting(format);
    setError(null);
    setWarning(null);
    try {
      const { publicUrl } = await api.exportFullResume(format, resumename || name.trim(), resume);
      window.open(publicUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `Failed to export ${format === 'pdf' ? 'PDF' : 'text'}.`;
      setError(msg);
    } finally {
      setExporting(null);
    }
  }

  // Prevent modal close during import
  function handleClose() {
    if (importing) return;
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    setTagGenPhase('idle');
    // If the "All recommendations have been implemented" banner is currently
    // applicable, remember that the user has seen it so it won't reappear on
    // the next open (re-armed only by running a fresh AI Review).
    try {
      if (recsAckKey && hasReviewedBefore && !hasRecommendations) {
        localStorage.setItem(recsAckKey, '1');
      }
    } catch { /* ignore */ }
    onClose();
  }

  // ---- Resume ▾ → Switch resumes / New resume -----------------------------
  // Both navigations abandon the current draft, so an unsaved editor asks
  // first (the confirm below); a clean editor goes straight through.
  function requestNav(nav: { kind: 'switch'; resumename: string } | { kind: 'new' }) {
    if (importing || saving) return;
    setResumeMenuOpen(false);
    if (isDirty) {
      setPendingNav(nav);
      return;
    }
    runNav(nav);
  }

  function runNav(nav: { kind: 'switch'; resumename: string } | { kind: 'new' }) {
    setPendingNav(null);
    // Stop any in-flight tag poll for the resume we're leaving.
    if (tagPollRef.current) tagPollRef.current.cancelled = true;
    setTagGenPhase('idle');
    setError(null);
    setWarning(null);
    if (nav.kind === 'switch') {
      // Parent re-points `resumename`; the [isOpen, resumename] effect reloads
      // through the same path used when the editor is opened for a resume.
      onSwitchResume?.(nav.resumename);
      return;
    }
    // New resume: hand off to the app's existing create flow (quota checks
    // live there), which reopens the editor in create-mode.
    handleClose();
    onNewResume?.();
  }

  async function handleClone() {
    if (!resumename) return;
    setCloning(true);
    setError(null);
    try {
      // Generate clone name: "Resume Name (1)", or increment if that exists
      const baseName = name || resumename;
      const match = baseName.match(/^(.*)\s*\((\d+)\)$/);
      let cloneName: string;
      if (match) {
        cloneName = `${match[1]} (${parseInt(match[2], 10) + 1})`;
      } else {
        cloneName = `${baseName} (1)`;
      }
      await api.createResume(cloneName, withSectionSettings(resume));
      onClose();
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to clone resume';
      setError(msg);
    } finally {
      setCloning(false);
    }
  }

  async function handleDeleteResume() {
    if (!resumename || !onDeleteResume) return;
    setDeleting(true);
    try {
      await onDeleteResume(resumename);
      setShowDeleteConfirm(false);
      onClose();
      onSuccess();
    } catch (err) {
      console.error('Failed to delete resume:', err);
      setError('Failed to delete resume');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  }

  // ---- Per-section editor bodies -------------------------------------
  // The lightbox renders these in the per-resume section order (draft
  // state below), so the arrows beside each heading reorder both the
  // editor and the generated resume. Bodies are unchanged from when they
  // were inline — only their wrapper moved.
  function renderSummarySection() {
    return (
      <>
          {/* Summary */}
          <div data-help-target="mainapp.resumes.resumeN.summary" className={sectionGenTypes.summary === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('summary', 'mb-1')}
            <textarea
              rows={5}
              value={resume.summary}
              onChange={(e) => setResume({ ...resume, summary: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg"
              style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH + 26 }}
              placeholder="Brief professional summary..."
            />
            {renderInlineRecs('summary')}
          </div>

          {/* Summary Phrases */}
          <div className="mt-3">
            <label className="block text-sm font-semibold text-gray-600 mb-1">Summary Phrases</label>
            <p className="text-xs text-gray-500 mb-2">
              Optional. Define consistent phrasing that's selected as a unit when building the summary section.
              Phrases are used verbatim, so craft them carefully — they're ranked by tag relevance, and low-match phrases are excluded automatically.
            </p>
            {(resume.summary_phrases || []).length > 0 && (
              <div className="space-y-2 mb-2">
                {(resume.summary_phrases || []).map((phrase, i) => (
                  <div key={i} data-phrase-index={i} className={`flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100${phrase.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={phrase.include !== '0'}
                      onChange={(e) => {
                        const phrases = [...(resume.summary_phrases || [])];
                        phrases[i] = { ...phrases[i], include: e.target.checked ? '1' : '0' };
                        setResume({ ...resume, summary_phrases: phrases });
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3 h-3"
                      title={phrase.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    <div className="flex-1 min-w-0">
                      {editingSummaryPhraseIndex === i ? (
                        <>
                          <textarea
                            value={phrase.text}
                            onChange={(e) => updateSummaryPhraseText(i, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); setEditingSummaryPhraseIndex(null); } }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-1"
                            rows={2}
                            autoFocus
                          />
                          <input
                            key={(phrase.tags || []).join(',')}
                            type="text"
                            defaultValue={(phrase.tags || []).join(', ')}
                            onBlur={(e) => updateSummaryPhraseTags(i, e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateSummaryPhraseTags(i, e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean));
                                setEditingSummaryPhraseIndex(null);
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                            placeholder="tags (comma-separated)"
                          />
                        </>
                      ) : (
                        <div onClick={() => setEditingSummaryPhraseIndex(i)} className="cursor-pointer">
                          <div className="text-sm text-gray-700 leading-snug">{phrase.text}</div>
                          {(phrase.tags || []).length > 0 && (
                            <div className="text-[10px] text-gray-400 truncate leading-tight mt-0.5">{phrase.tags.join(', ')}</div>
                          )}
                        </div>
                      )}
                    </div>
                    <button onClick={() => { if (editingSummaryPhraseIndex === i) setEditingSummaryPhraseIndex(null); removeSummaryPhrase(i); }} className="text-gray-400 hover:text-red-500 shrink-0 mt-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input type="text" value={newSummaryPhrase} onChange={(e) => setNewSummaryPhrase(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSummaryPhrase()} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Add summary phrase..." autoComplete="off" />
              <button onClick={addSummaryPhrase} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderHighlightsSection() {
    return (
      <>
          {/* Highlights — no editable content; the bullets are selected/written
              per job at generation time. Only the heading (name/order) and the
              generation-type pill are set here, like every other section. */}
          <div data-help-target="mainapp.resumes.resumeN.highlights" className={sectionGenTypes.highlights === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('highlights', 'mb-1')}
            <p className="text-xs text-gray-500 mb-2">
              {sectionGenTypes.highlights === 'embedding'
                ? 'AI Match selects the 3 most relevant bullets from your Experience section — regardless of company or role — and uses each one verbatim as a highlight, with the company cited at the end. Those bullets also remain in their Company/Role section. AI does NOT modify the text, so be sure every experience bullet is well-formed and polished.'
                : sectionGenTypes.highlights === 'generate'
                ? 'AI Generate writes 3 brand-new highlight bullets tailored to each job description, drawing on your highest-matching experience and combining achievements as needed. The wording is freshly composed by AI rather than copied, so review the results for accuracy before applying.'
                : 'Highlights will be omitted — no highlights section will appear on generated resumes.'}
            </p>
          </div>
      </>
    );
  }

  function renderSkillsSection() {
    return (
      <>
          {/* Skills */}
          <div data-help-target="mainapp.resumes.resumeN.skills" className={sectionGenTypes.skills === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('skills', 'mb-2')}
            {/* Gen-type-aware description of how skills render on the bespoke resume. */}
            <p className="text-xs text-gray-500 mb-2">
              {sectionGenTypes.skills === 'embedding' && (
                <>
                  AI Match will match the skill groups and skills against each job description, then display
                  only the most relevant skills for each job (highest match first). The skill group name is also
                  tweaked to best fit the job description language, to make the categories sound familiar to the
                  hiring manager. If a skill group has low relevance, but one particular skill has high relevance,
                  that skill can be added to an &quot;Other&quot; category, to ensure it&apos;s represented.
                </>
              )}
              {sectionGenTypes.skills === 'manual' && (
                <>
                  Will display ALL active (checked) skills. To remove a skill from the resume, simply uncheck the
                  skill below. If you uncheck ALL skills from a particular skill group, that group will not appear
                  on the resume.
                </>
              )}
              {sectionGenTypes.skills === 'omit' && (
                <>The Skills section will not appear on generated resumes.</>
              )}
            </p>
            {/* Conflict pointer (task 8): full copy is in the top error banner; this
                is the local cue tying the red highlight to the synonym clash. */}
            {conflictSetIds.size > 0 && (
              <div className="mb-2 text-xs text-red-600 font-medium">
                ⚠ Skills highlighted in red clash with a synonym set — see the note at the top.
              </div>
            )}
            {(() => {
              const groups = (resume.skill_groups || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
              const groupIdSet = new Set(groups.map(g => g.id));
              const indexed = resume.skills.map((skill, i) => ({ skill, i }));
              const ungrouped = indexed.filter(x => !x.skill.group_id || !groupIdSet.has(x.skill.group_id as string));
              const dropHandlers = (gid: string | null) => ({
                onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverGroup(gid ?? '__ungrouped__'); },
                onDragLeave: () => setDragOverGroup(null),
                onDrop: (e: React.DragEvent) => {
                  e.preventDefault();
                  const raw = e.dataTransfer.getData('text/plain');
                  const idx = draggedSkillIdx ?? (raw ? parseInt(raw, 10) : NaN);
                  if (!isNaN(idx)) moveSkillToGroup(idx, gid);
                  setDraggedSkillIdx(null); setDragOverGroup(null);
                },
              });

              if (groups.length === 0) {
                // No groups yet — flat list (legacy look); "Add group" below starts grouping.
                return (
                  <div className="mb-2">
                    <div className="flex flex-wrap gap-2" {...dropHandlers(null)}>
                      {ungrouped.map(({ skill, i }) => renderSkillCard(skill, i))}
                    </div>
                    {renderGroupAddInput('')}
                  </div>
                );
              }

              return (
                <div className="space-y-2 mb-2">
                  {groups.map(g => {
                    const items = indexed.filter(x => x.skill.group_id === g.id);
                    const hot = dragOverGroup === g.id;
                    return (
                      <div key={g.id} {...dropHandlers(g.id)} className={`border rounded-lg p-2 transition-colors ${hot ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          {editingGroupId === g.id ? (
                            <input autoFocus value={g.title} onChange={(e) => updateSkillGroupTitle(g.id, e.target.value)} onBlur={() => setEditingGroupId(null)} onKeyDown={(e) => e.key === 'Enter' && setEditingGroupId(null)} placeholder="Group name…" className="text-sm font-semibold px-1.5 py-0.5 border border-gray-300 rounded flex-1 min-w-0" autoComplete="off" />
                          ) : (
                            <button onClick={() => setEditingGroupId(g.id)} className="text-sm font-semibold text-gray-700 hover:text-primary-600 truncate" title="Click to rename">
                              {g.title || <span className="text-gray-400 italic font-normal">Untitled group</span>}
                            </button>
                          )}
                          <span className="text-xs text-gray-400 shrink-0">{items.length}</span>
                          <div className="flex-1" />
                          <button onClick={() => removeSkillGroup(g.id)} title="Delete group (its skills become ungrouped)" className="text-gray-400 hover:text-red-500 shrink-0">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {items.length > 0 ? items.map(({ skill, i }) => renderSkillCard(skill, i))
                            : <span className="text-xs text-gray-400 italic py-1">Drag skills here</span>}
                        </div>
                        {renderGroupAddInput(g.id)}
                      </div>
                    );
                  })}
                  {/* Ungrouped lane — always a drop target so skills can be pulled back out. */}
                  <div {...dropHandlers(null)} className={`border border-dashed rounded-lg p-2 transition-colors ${dragOverGroup === '__ungrouped__' ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-semibold text-gray-500">Ungrouped</span>
                      <span className="text-xs text-gray-400">{ungrouped.length}</span>
                      <span className="text-[11px] text-gray-400 italic">— auto-sorted into your groups on save</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ungrouped.length > 0 ? ungrouped.map(({ skill, i }) => renderSkillCard(skill, i))
                        : <span className="text-xs text-gray-400 italic py-1">Drag skills here to ungroup</span>}
                    </div>
                    {renderGroupAddInput('')}
                  </div>
                </div>
              );
            })()}
            {resume.skills.map((_, i) => (
              <div key={`skill-rec-${i}`}>{renderInlineRecs(`skills[${i}]`)}</div>
            ))}
            {/* Section-level and out-of-range skills recommendations */}
            {renderInlineRecs('skills')}
            {(() => {
              const indexedPaths = new Set(resume.skills.map((_, i) => `skills[${i}]`));
              indexedPaths.add('skills');
              const unmatchedPaths = [...new Set(
                pendingRecs
                  .filter(r => (r.path === 'skills' || r.path.startsWith('skills[')) && !indexedPaths.has(r.path))
                  .map(r => r.path)
              )];
              return unmatchedPaths.map(p => <div key={`extra-skill-${p}`}>{renderInlineRecs(p)}</div>);
            })()}
            <button onClick={addSkillGroup} className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200" title="Create a new skill group">
              <Plus className="w-4 h-4" /> Add group
            </button>
          </div>
      </>
    );
  }

  function renderExperienceSection() {
    return (
      <>
          {/* Experience */}
          <div data-help-target="mainapp.resumes.resumeN.experience" className={sectionGenTypes.experience === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('experience', 'mb-2')}
            <div className="space-y-4 mb-3">
              {getExperience().map((company, companyIdx) => (
                <div key={companyIdx} className={`border border-gray-200 rounded-lg p-3${company.include === '0' ? ' opacity-40' : ''}`}>
                  {/* Company Header */}
                  <div className="flex items-start gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={company.include !== '0'}
                      onChange={(e) => updateCompany(companyIdx, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0"
                      title={company.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    <Building2 className="w-5 h-5 text-gray-500 mt-1" />
                    <div className="flex-1">
                      {editingCompanyIndex === companyIdx ? (
                        <div className="space-y-2">
                          <input type="text" value={company.company} onChange={(e) => updateCompany(companyIdx, { company: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company Name" />
                          <div className="grid grid-cols-3 gap-2">
                            <input type="text" value={(company.url || []).join(', ')} onChange={(e) => updateCompany(companyIdx, { url: e.target.value.split(',').map(u => u.trim()).filter(Boolean) })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Company URL(s), comma-separated" />
                            <input type="text" value={company.employees || ''} onChange={(e) => updateCompany(companyIdx, { employees: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="# Employees" />
                            <input type="text" value={company.location || ''} onChange={(e) => updateCompany(companyIdx, { location: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location" />
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <input type="text" value={company.startDate} onChange={(e) => updateCompany(companyIdx, { startDate: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Start Year / Month" />
                            <input type="text" value={company.endDate || ''} onChange={(e) => updateCompany(companyIdx, { endDate: e.target.value })} disabled={company.current} className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100" placeholder="End Year / Month" />
                            <label className="flex items-center text-sm">
                              <input type="checkbox" checked={company.current} onChange={(e) => updateCompany(companyIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : company.endDate })} className="mr-2" />
                              Current
                            </label>
                          </div>
                          <textarea value={company.description || ''} onChange={(e) => updateCompany(companyIdx, { description: e.target.value })} className="px-2 py-1 border border-gray-300 rounded" rows={2} placeholder="Short company description" style={{ fontFamily: RESUME_FONT, fontSize: '10.5pt', lineHeight: 1.45, width: RESUME_WIDTH }} />
                          <button onClick={() => setEditingCompanyIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                        </div>
                      ) : (
                        <div>
                          <span className="font-medium">{company.company || 'New Company'}</span>
                          {company.location && <span className="text-gray-500 text-sm ml-2">({company.location})</span>}
                          <div className="text-xs text-gray-500">{company.startDate} - {company.current ? 'Present' : company.endDate}</div>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditingCompanyIndex(editingCompanyIndex === companyIdx ? null : companyIdx)} className="text-gray-500 hover:text-gray-600">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveCompany(companyIdx, 'up')} disabled={companyIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => moveCompany(companyIdx, 'down')} disabled={companyIdx === getExperience().length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeCompany(companyIdx)} className="text-gray-500 hover:text-red-500">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Company-level recommendations (company name / description) */}
                  {renderInlineRecs(`experience[${companyIdx}]`)}

                  {/* Roles */}
                  <div className="ml-6 space-y-3">
                    {company.roles.map((role, roleIdx) => (
                      <div key={roleIdx} className={`border-l-2 border-gray-200 pl-3${role.include === '0' ? ' opacity-50' : ''}`}>
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={role.include !== '0'}
                            onChange={(e) => updateRole(companyIdx, roleIdx, { include: e.target.checked ? '1' : '0' })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 shrink-0 w-3.5 h-3.5"
                            title={role.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                          />
                          <Briefcase className="w-4 h-4 text-gray-500 mt-1" />
                          <div className="flex-1">
                            {editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? (
                              <div className="space-y-2">
                                <input type="text" value={role.title} onChange={(e) => updateRole(companyIdx, roleIdx, { title: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Role Title" />
                                <div className="grid grid-cols-3 gap-2">
                                  <input type="text" value={role.startDate} onChange={(e) => updateRole(companyIdx, roleIdx, { startDate: e.target.value })} className="px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Start Year / Month" />
                                  <input type="text" value={role.endDate || ''} onChange={(e) => updateRole(companyIdx, roleIdx, { endDate: e.target.value })} disabled={role.current} className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100" placeholder="End Year / Month" />
                                  <label className="flex items-center text-sm">
                                    <input type="checkbox" checked={role.current} onChange={(e) => updateRole(companyIdx, roleIdx, { current: e.target.checked, endDate: e.target.checked ? undefined : role.endDate })} className="mr-2" />
                                    Current
                                  </label>
                                </div>
                                <input type="text" value={role.location || ''} onChange={(e) => updateRole(companyIdx, roleIdx, { location: e.target.value })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Location (if different from company)" />
                                <button onClick={() => setEditingRoleIndex(null)} className="text-xs text-primary-600 hover:underline">Done editing</button>
                              </div>
                            ) : (
                              <div>
                                <span className="text-sm font-medium">{role.title || 'New Role'}</span>
                                <div className="text-xs text-gray-500">{role.startDate} - {role.current ? 'Present' : role.endDate}</div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => setEditingRoleIndex(editingRoleIndex?.companyIdx === companyIdx && editingRoleIndex?.roleIdx === roleIdx ? null : { companyIdx, roleIdx })} className="text-gray-500 hover:text-gray-600">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveRole(companyIdx, roleIdx, 'up')} disabled={roleIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveRole(companyIdx, roleIdx, 'down')} disabled={roleIdx === company.roles.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                              <ChevronDown className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeRole(companyIdx, roleIdx)} className="text-gray-500 hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Bullets */}
                        <div className="ml-5 mt-2 space-y-1">
                          {role.bullets.map((bullet, bulletIdx) => (
                            <div key={bulletIdx} className={bullet.include === '0' ? 'opacity-50' : ''}>
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={bullet.include !== '0'}
                                  onChange={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { include: e.target.checked ? '1' : '0' })}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1.5 shrink-0 w-3 h-3"
                                  title={bullet.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                                />
                                <span className="text-gray-500 mt-1">•</span>
                                <div className="flex-1">
                                  <BulletTextWithLineBreaks
                                    text={bullet.text}
                                    onChange={(text) => updateBullet(companyIdx, roleIdx, bulletIdx, { text })}
                                  />
                                  <input
                                    key={(bullet.tags || []).join(',')}
                                    type="text"
                                    defaultValue={(bullet.tags || []).join(', ')}
                                    onBlur={(e) => updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        updateBullet(companyIdx, roleIdx, bulletIdx, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500 mt-1"
                                    placeholder="Tags (comma-separated)"
                                  />
                                  {renderTagStatusChip(bullet.tags, bullet.text, TAG_MIN_BULLET)}
                                </div>
                                <div className="flex gap-1">
                                  <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'up')} disabled={bulletIdx === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronUp className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => moveBullet(companyIdx, roleIdx, bulletIdx, 'down')} disabled={bulletIdx === role.bullets.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                                    <ChevronDown className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => removeBullet(companyIdx, roleIdx, bulletIdx)} className="text-gray-500 hover:text-red-500">
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                              {renderInlineRecs(`experience[${companyIdx}].roles[${roleIdx}].bullets[${bulletIdx}]`)}
                            </div>
                          ))}
                          <button onClick={() => addBullet(companyIdx, roleIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add bullet
                          </button>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => addRole(companyIdx)} className="text-xs text-primary-600 hover:underline flex items-center gap-1 ml-1">
                      <Plus className="w-3 h-3" /> Add role
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addCompany} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">
              <Plus className="w-4 h-4" /> Add Company
            </button>
          </div>
      </>
    );
  }

  function renderEducationSection() {
    return (
      <>
          {/* Education */}
          <div data-help-target="mainapp.resumes.resumeN.education" className={sectionGenTypes.education === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('education', 'mb-2')}
            <div className="space-y-2 mb-3">
              {resume.education.map((edu, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${edu.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={edu.include !== '0'}
                      onChange={(e) => updateEducation(i, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={edu.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingEducationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={edu.title} onChange={(e) => updateEducation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title/Degree" />
                          <input type="text" value={edu.org} onChange={(e) => updateEducation(i, { org: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Organization" />
                          <input type="text" value={edu.url || ''} onChange={(e) => updateEducation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={edu.year || ''} onChange={(e) => updateEducation(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                        </div>
                        <input
                          key={(edu.tags || []).join(',')}
                          type="text"
                          defaultValue={(edu.tags || []).join(', ')}
                          onBlur={(e) => updateEducation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateEducation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(edu.tags, edu.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{edu.title}</span>
                        <span className="text-sm text-gray-500"> — {edu.org}</span>
                        {edu.year && <span className="text-xs text-gray-500 ml-2">({edu.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(edu.tags, edu.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingEducationIndex(editingEducationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveEducation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveEducation(i, 'down')} disabled={i === resume.education.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeEducation(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`education[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title/Degree</label>
                <input type="text" value={newEduTitle} onChange={(e) => setNewEduTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="B.S. Computer Science" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Organization</label>
                <input type="text" value={newEduOrg} onChange={(e) => setNewEduOrg(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="University Name" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newEduYear} onChange={(e) => setNewEduYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addEducation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2020" />
              </div>
              <button onClick={addEducation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderAwardsSection() {
    return (
      <>
          {/* Awards */}
          <div data-help-target="mainapp.resumes.resumeN.awards" className={sectionGenTypes.awards === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('awards', 'mb-2')}
            <div className="space-y-2 mb-3">
              {resume.awards.map((award, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${award.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={award.include !== '0'}
                      onChange={(e) => updateAward(i, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={award.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingAwardIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={award.title} onChange={(e) => updateAward(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={award.issuer || ''} onChange={(e) => updateAward(i, { issuer: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Issuer" />
                          <input type="text" value={award.year || ''} onChange={(e) => updateAward(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                        </div>
                        <div className="flex gap-2 items-center">
                          <input type="text" value={award.reward || ''} onChange={(e) => updateAward(i, { reward: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Reward" />
                          <input type="text" value={award.url || ''} onChange={(e) => updateAward(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                        </div>
                        <input
                          key={(award.tags || []).join(',')}
                          type="text"
                          defaultValue={(award.tags || []).join(', ')}
                          onBlur={(e) => updateAward(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updateAward(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(award.tags, award.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{award.title}</span>
                        {award.issuer && <span className="text-sm text-gray-500"> — {award.issuer}</span>}
                        {award.reward && <span className="text-sm text-gray-500"> — {award.reward}</span>}
                        {award.year && <span className="text-xs text-gray-500 ml-2">({award.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(award.tags, award.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingAwardIndex(editingAwardIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveAward(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => moveAward(i, 'down')} disabled={i === resume.awards.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removeAward(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`awards[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newAwardTitle} onChange={(e) => setNewAwardTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Award Name" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Issuer</label>
                <input type="text" value={newAwardIssuer} onChange={(e) => setNewAwardIssuer(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Awarding organization" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Reward</label>
                <input type="text" value={newAwardReward} onChange={(e) => setNewAwardReward(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Reward provided" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newAwardYear} onChange={(e) => setNewAwardYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAward()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addAward} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderPresentationsSection() {
    return (
      <>
          {/* Presentations */}
          <div data-help-target="mainapp.resumes.resumeN.presentations" className={sectionGenTypes.presentations === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('presentations', 'mb-2')}
            <div className="space-y-2 mb-3">
              {(resume.presentations || []).map((presentation, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${presentation.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={presentation.include !== '0'}
                      onChange={(e) => updatePresentation(i, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={presentation.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPresentationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={presentation.title} onChange={(e) => updatePresentation(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={presentation.event || ''} onChange={(e) => updatePresentation(i, { event: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Event/Location" />
                          <input type="text" value={presentation.url || ''} onChange={(e) => updatePresentation(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={presentation.year || ''} onChange={(e) => updatePresentation(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                        </div>
                        <input
                          key={(presentation.tags || []).join(',')}
                          type="text"
                          defaultValue={(presentation.tags || []).join(', ')}
                          onBlur={(e) => updatePresentation(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePresentation(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(presentation.tags, presentation.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        <span className="text-sm font-medium">{presentation.title}</span>
                        {presentation.event && <span className="text-sm text-gray-500"> — {presentation.event}</span>}
                        {presentation.year && <span className="text-xs text-gray-500 ml-2">({presentation.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(presentation.tags, presentation.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPresentationIndex(editingPresentationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePresentation(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePresentation(i, 'down')} disabled={i === (resume.presentations || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePresentation(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`presentations[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPresentationTitle} onChange={(e) => setNewPresentationTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Presentation or Keynote Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Event/Location</label>
                <input type="text" value={newPresentationEvent} onChange={(e) => setNewPresentationEvent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Event name or location" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPresentationYear} onChange={(e) => setNewPresentationYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPresentation()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPresentation} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderPublicationsSection() {
    return (
      <>
          {/* Publications */}
          <div data-help-target="mainapp.resumes.resumeN.publications" className={sectionGenTypes.publications === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('publications', 'mb-2')}
            <div className="space-y-2 mb-3">
              {(resume.publications || []).map((pub, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${pub.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={pub.include !== '0'}
                      onChange={(e) => updatePublication(i, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={pub.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPublicationIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={pub.title} onChange={(e) => updatePublication(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={pub.url || ''} onChange={(e) => updatePublication(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={pub.year || ''} onChange={(e) => updatePublication(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                        </div>
                        <input
                          key={(pub.tags || []).join(',')}
                          type="text"
                          defaultValue={(pub.tags || []).join(', ')}
                          onBlur={(e) => updatePublication(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePublication(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(pub.tags, pub.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {pub.url ? (
                          <a href={pub.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{pub.title}</a>
                        ) : (
                          <span className="text-sm font-medium">{pub.title}</span>
                        )}
                        {pub.year && <span className="text-xs text-gray-500 ml-2">({pub.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(pub.tags, pub.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPublicationIndex(editingPublicationIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePublication(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePublication(i, 'down')} disabled={i === (resume.publications || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePublication(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`publications[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPubTitle} onChange={(e) => setNewPubTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Publication Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                <input type="text" value={newPubUrl} onChange={(e) => setNewPubUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/article" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPubYear} onChange={(e) => setNewPubYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPublication()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPublication} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderPortfoliosSection() {
    return (
      <>
          {/* Portfolios */}
          <div data-help-target="mainapp.resumes.resumeN.portfolios" className={sectionGenTypes.portfolios === 'omit' ? 'opacity-50' : ''}>
            {renderSectionHeader('portfolios', 'mb-2')}
            <div className="space-y-2 mb-3">
              {(resume.portfolios || []).map((port, i) => (
                <div key={i}>
                  <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${port.include === '0' ? ' opacity-40' : ''}`}>
                    <input
                      type="checkbox"
                      checked={port.include !== '0'}
                      onChange={(e) => updatePortfolio(i, { include: e.target.checked ? '1' : '0' })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                      title={port.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                    />
                    {editingPortfolioIndex === i ? (
                      <div className="flex-1 space-y-1">
                        <div className="flex gap-2 items-center">
                          <input type="text" value={port.title} onChange={(e) => updatePortfolio(i, { title: e.target.value })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" />
                          <input type="text" value={port.url || ''} onChange={(e) => updatePortfolio(i, { url: e.target.value || undefined })} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL (optional)" />
                          <input type="text" value={port.year || ''} onChange={(e) => updatePortfolio(i, { year: e.target.value })} className="w-24 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Year" />
                        </div>
                        <input
                          key={(port.tags || []).join(',')}
                          type="text"
                          defaultValue={(port.tags || []).join(', ')}
                          onBlur={(e) => updatePortfolio(i, { tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePortfolio(i, { tags: e.currentTarget.value.split(',').map(t => t.trim()).filter(Boolean) });
                              e.currentTarget.blur();
                            }
                          }}
                          className="w-full px-2 py-0.5 border border-gray-100 rounded text-xs text-gray-500"
                          placeholder="Tags (comma-separated)"
                        />
                        {renderTagStatusChip(port.tags, port.title)}
                      </div>
                    ) : (
                      <div className="flex-1">
                        {port.url ? (
                          <a href={port.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary-600 hover:underline">{port.title}</a>
                        ) : (
                          <span className="text-sm font-medium">{port.title}</span>
                        )}
                        {port.year && <span className="text-xs text-gray-500 ml-2">({port.year})</span>}
                        <span className="ml-2">{renderTagStatusChip(port.tags, port.title)}</span>
                      </div>
                    )}
                    <button onClick={() => setEditingPortfolioIndex(editingPortfolioIndex === i ? null : i)} className="text-gray-500 hover:text-gray-600">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePortfolio(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button onClick={() => movePortfolio(i, 'down')} disabled={i === (resume.portfolios || []).length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button onClick={() => removePortfolio(i)} className="text-gray-500 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderInlineRecs(`portfolios[${i}]`)}
                </div>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Title</label>
                <input type="text" value={newPortTitle} onChange={(e) => setNewPortTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Portfolio Title" />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">URL (optional)</label>
                <input type="text" value={newPortUrl} onChange={(e) => setNewPortUrl(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="https://example.com/portfolio" />
              </div>
              <div className="w-24">
                <label className="block text-xs text-gray-500 mb-1">Year</label>
                <input type="text" value={newPortYear} onChange={(e) => setNewPortYear(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addPortfolio()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="2023" />
              </div>
              <button onClick={addPortfolio} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
      </>
    );
  }

  function renderSection(component: SubcomponentType) {
    switch (component) {
      case 'summary': return renderSummarySection();
      case 'highlights': return renderHighlightsSection();
      case 'skills': return renderSkillsSection();
      case 'experience': return renderExperienceSection();
      case 'education': return renderEducationSection();
      case 'awards': return renderAwardsSection();
      case 'presentations': return renderPresentationsSection();
      case 'publications': return renderPublicationsSection();
      case 'portfolios': return renderPortfoliosSection();
      default: return null;
    }
  }

  if (!isOpen) return null;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label={resumename ? 'Edit Resume' : 'Create Resume'} data-help-target="mainapp.resumes.resumeN">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-[1232px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* ── Top bar: title + every resume-level action (mockup 20). ── */}
        <header className="flex-shrink-0 min-h-[60px] flex items-center gap-2 pl-5 pr-3 py-2 bg-white border-b border-gray-200">
          {/* Eyebrow + inline-editable resume name (the required Resume Name). */}
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="hidden lg:inline text-[11px] font-semibold uppercase tracking-[0.07em] text-gray-500 whitespace-nowrap">
              {resumename ? 'Edit resume' : 'New resume'}
            </span>
            {editingName ? (
              <input
                ref={nameInputRef}
                type="text"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commitName(); }
                  if (e.key === 'Escape') { cancelNameEditRef.current = true; setEditingName(false); }
                }}
                data-help-target="mainapp.resumes.resumeN.resumename"
                data-testid="resume-name-input"
                className="w-[212px] max-w-[36vw] px-2.5 py-1.5 rounded-lg border border-primary-500 bg-white text-[15px] font-semibold tracking-[-0.01em] text-gray-900 outline-none ring-[3px] ring-primary-100"
                placeholder="My Professional Resume"
                aria-label="Resume name (required)"
                autoComplete="off"
                autoFocus
              />
            ) : (
              <button
                type="button"
                onClick={startEditName}
                disabled={importing || loading}
                data-help-target="mainapp.resumes.resumeN.resumename"
                data-testid="resume-name-inline"
                className="group flex items-center gap-1.5 min-w-0 px-2 py-1 -ml-2 rounded-lg border border-transparent hover:bg-gray-50 hover:border-gray-200 disabled:opacity-60 transition-colors"
                title="Resume name (required) — an internal label to help you tell your resumes apart; it is never printed on the resume."
              >
                <Edit2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                <span className={`truncate text-[15px] font-semibold tracking-[-0.01em] ${name.trim() ? 'text-gray-900' : 'text-amber-700'}`}>
                  {name.trim() || 'Name this resume'}
                </span>
              </button>
            )}
          </div>

          <span className="flex-1 min-w-[8px]" />

          <div className="flex items-center gap-[5px]">
            {(() => {
              const hasResumeData = !!(resume.name || resume.skills.length > 0 || resume.summary || getExperience().length > 0);
              const busy = importing || loading || exporting !== null;
              return (
                <>
                  {/* Import ▾ — Load from PDF / Load from file */}
                  <div ref={importMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setImportMenuOpen(o => !o)}
                      disabled={busy}
                      data-help-target="mainapp.resumes.resumeN.import"
                      className={TB_OUT}
                      title="Import resume data (PDF, YAML or JSON)"
                      aria-haspopup="menu"
                      aria-expanded={importMenuOpen}
                    >
                      <Upload className="w-[15px] h-[15px]" />
                      <span className="hidden xl:inline">Import</span>
                      <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${importMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {importMenuOpen && (
                      <div role="menu" className={TB_MENU}>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setImportMenuOpen(false); handleLoadFromFileClick(); }}
                          data-help-target="mainapp.resumes.resumeN.loadfrompdf"
                          className={TB_MENU_ITEM}
                        >
                          <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">Load from PDF</span>
                            <span className="block text-[11.5px] text-gray-500">Parse an existing resume PDF</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setImportMenuOpen(false); handleLoadFromFileClick(); }}
                          data-help-target="mainapp.resumes.resumeN.import.fromfile"
                          className={TB_MENU_ITEM}
                        >
                          <Save className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">Load from file</span>
                            <span className="block text-[11.5px] text-gray-500">YAML or JSON resume data</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Export ▾ — PDF / Text File / Data File */}
                  <div ref={exportMenuRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setExportMenuOpen(o => !o)}
                      disabled={busy}
                      data-help-target="mainapp.resumes.resumeN.export"
                      className={TB_OUT}
                      title="Export this resume (PDF, Text File, or Data File)"
                      aria-haspopup="menu"
                      aria-expanded={exportMenuOpen}
                    >
                      {exporting !== null ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Download className="w-[15px] h-[15px]" />}
                      <span className="hidden xl:inline">Export</span>
                      <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {exportMenuOpen && (
                      <div role="menu" className={TB_MENU}>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleExportFull('pdf'); }}
                          disabled={!hasResumeData || exporting !== null}
                          data-help-target="mainapp.resumes.resumeN.export.pdf"
                          className={TB_MENU_ITEM}
                          title="Export the full resume (all sections, all items) as a PDF"
                        >
                          {exporting === 'pdf' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                          <span>
                            <span className="block font-medium">PDF</span>
                            <span className="block text-[11.5px] text-gray-500">Every section, nothing trimmed</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleExportFull('text'); }}
                          disabled={!hasResumeData || exporting !== null}
                          data-help-target="mainapp.resumes.resumeN.export.text"
                          className={TB_MENU_ITEM}
                          title="Export the full resume (all sections, all items) as a plain-text file"
                        >
                          {exporting === 'text' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileType className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                          <span>
                            <span className="block font-medium">Text File</span>
                            <span className="block text-[11.5px] text-gray-500">Plain text for ATS paste-in</span>
                          </span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setExportMenuOpen(false); handleDownloadTemplate(); }}
                          data-help-target="mainapp.resumes.resumeN.export.datafile"
                          className={TB_MENU_ITEM}
                          title={hasResumeData ? 'Export resume data as a YAML datafile' : 'Download a blank YAML template'}
                        >
                          <Download className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>
                            <span className="block font-medium">{hasResumeData ? 'Data File' : 'Data File Template'}</span>
                            <span className="block text-[11.5px] text-gray-500">YAML backup of this resume</span>
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            {resumename && (
              <>
                <span className={TB_VR} aria-hidden="true" />

                {/* Review resume — runs the AI review (was a banner-row button). */}
                <button
                  type="button"
                  onClick={handleAIReview}
                  disabled={reviewing || importing || loading || saving}
                  data-help-target="mainapp.resumes.resumeN.aireview"
                  data-testid="review-resume"
                  className={TB_PRIMARY}
                  title={
                    reviewing
                      ? 'Reviewing…'
                      : (reviewComplete || hasReviewedBefore)
                        ? 'Review complete — click to run the AI review again'
                        : 'Review Resume and Generate Recommendations'
                  }
                >
                  {reviewing
                    ? <Loader2 className="w-[15px] h-[15px] animate-spin" />
                    : (reviewComplete || hasReviewedBefore)
                      ? <Check className="w-[15px] h-[15px]" />
                      : <Sparkles className="w-[15px] h-[15px]" />}
                  <span>{reviewing ? 'Reviewing…' : 'Review resume'}</span>
                </button>

                {/* Scorecard NN — opens the (read-only) scorecard modal. */}
                <button
                  type="button"
                  onClick={() => setShowScorecard(true)}
                  disabled={importing || loading}
                  data-help-target="mainapp.resumes.resumeN.scorecard"
                  data-testid="scorecard-chip"
                  className={TB_GHOST}
                  title="View Resume Scorecard — a deterministic read of how your resume comes across, including an Authenticity (human-voice) score"
                >
                  <Gauge className="w-[15px] h-[15px]" />
                  <span className="hidden xl:inline">Scorecard</span>
                  <span
                    className={`inline-flex items-center justify-center min-w-[26px] h-[19px] px-1.5 rounded-full border text-[11px] font-bold tabular-nums ${
                      scorecardScore >= 80
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : scorecardScore >= 60
                          ? 'bg-amber-50 border-amber-200 text-amber-700'
                          : 'bg-red-50 border-red-200 text-red-700'
                    }`}
                  >
                    {scorecardScore}
                  </span>
                </button>
              </>
            )}

            <span className={TB_VR} aria-hidden="true" />

            {/* Live-preview toggle. */}
            <button
              type="button"
              onClick={() => setPreviewOpen(o => !o)}
              aria-pressed={previewOpen}
              data-help-target="mainapp.resumes.resumeN.preview"
              data-testid="preview-toggle"
              className={`hidden md:inline-flex ${TB_OUT} ${previewOpen ? 'bg-primary-50 border-primary-200 text-primary-700 hover:bg-primary-50' : ''}`}
              title={previewOpen ? 'Hide live preview' : 'Show live preview'}
            >
              <Eye className="w-[15px] h-[15px]" />
              <span>Preview</span>
            </button>

            {/* Resume ▾ — View HTML / View PDF / Clone / Delete. */}
            {resumename && (
              <div ref={resumeMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setResumeMenuOpen(o => !o)}
                  disabled={saving || importing || deleting || cloning || exporting !== null}
                  data-help-target="mainapp.resumes.resumeN.resumemenu"
                  className={TB_OUT}
                  title="Resume actions — View, Clone, or Delete"
                  aria-haspopup="menu"
                  aria-expanded={resumeMenuOpen}
                >
                  {(cloning || exporting === 'pdf') ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <SlidersHorizontal className="w-[15px] h-[15px]" />}
                  <span className="hidden lg:inline">Resume</span>
                  <ChevronDown className={`w-[13px] h-[13px] text-gray-400 transition-transform ${resumeMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {resumeMenuOpen && (
                  <div role="menu" className={`${TB_MENU} min-w-[256px]`}>
                    <div className={TB_MENU_GROUP}>This resume</div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setResumeMenuOpen(false);
                        if (user?.username) {
                          const url = `https://${user.username.toLowerCase()}.skillsnap.me/${masterResumePath}`;
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      disabled={!user?.username}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.viewhtml"
                      className={TB_MENU_ITEM}
                    >
                      <ExternalLink className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                      <span>View HTML</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setResumeMenuOpen(false); handleExportFull('pdf'); }}
                      disabled={exporting !== null}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.viewpdf"
                      className={TB_MENU_ITEM}
                    >
                      {exporting === 'pdf' ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                      <span>View PDF</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setResumeMenuOpen(false); handleClone(); }}
                      disabled={cloning || saving || importing || loading}
                      data-help-target="mainapp.resumes.resumeN.resumemenu.clone"
                      className={TB_MENU_ITEM}
                    >
                      {cloning ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Copy className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />}
                      <span>Clone Resume</span>
                    </button>
                    {onDeleteResume && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => { setResumeMenuOpen(false); setShowDeleteConfirm(true); }}
                          disabled={saving || importing || deleting}
                          data-help-target="mainapp.resumes.resumeN.resumemenu.delete"
                          className={`${TB_MENU_ITEM} text-red-700 hover:bg-red-50`}
                        >
                          <Trash2 className="w-[15px] h-[15px] text-red-600 flex-shrink-0" />
                          <span>Delete Resume</span>
                        </button>
                      </>
                    )}

                    {/* Switch resumes — the user's other resumes, from the same
                        list the header selector uses. With only one resume the
                        whole group is omitted (no empty state); "New Resume"
                        below always shows. */}
                    {onSwitchResume && switchResumes === null && switchLoading && (
                      <div className="flex items-center gap-2.5 px-2.5 py-[7px] text-[12.5px] text-gray-500">
                        <Loader2 className="w-[14px] h-[14px] animate-spin text-gray-400" />
                        <span>Loading resumes…</span>
                      </div>
                    )}

                    {onSwitchResume && otherResumes.length > 0 && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <div className={TB_MENU_GROUP}>Switch resumes</div>
                        <div className="max-h-[240px] overflow-y-auto" data-help-target="mainapp.resumes.resumeN.resumemenu.switchresumes" data-testid="switch-resumes">
                          {resumename && (
                            <div
                              className={`${TB_MENU_ITEM} bg-primary-50 text-primary-700 hover:bg-primary-50 cursor-default`}
                              data-help-target="mainapp.resumes.resumeN.resumemenu.switchresumes.current"
                              aria-current="true"
                            >
                              <FileText className="w-[15px] h-[15px] text-primary-600 flex-shrink-0" />
                              <span className="flex-1 min-w-0">
                                <span className="block truncate font-medium">{resumename}</span>
                                <span className="block text-[11.5px] text-primary-600/80">Currently editing</span>
                              </span>
                              <Check className="w-[14px] h-[14px] text-primary-600 flex-shrink-0" />
                            </div>
                          )}
                          {otherResumes.map((r, idx) => {
                            const meta = editedAgoLabel(r.lastupdate);
                            return (
                              <button
                                key={r.resumename}
                                type="button"
                                role="menuitem"
                                onClick={() => requestNav({ kind: 'switch', resumename: r.resumename })}
                                disabled={saving || importing || deleting || loading}
                                data-help-target={idx === 0 ? 'mainapp.resumes.resumeN.resumemenu.switchresumes.resume0' : undefined}
                                className={TB_MENU_ITEM}
                                title={`Switch the editor to "${r.resumename}"`}
                              >
                                <FileText className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                                <span className="flex-1 min-w-0">
                                  <span className="block truncate">{r.resumename}</span>
                                  {meta && <span className="block text-[11.5px] text-gray-500">{meta}</span>}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}

                    {onNewResume && (
                      <>
                        <div className="h-px bg-gray-200 my-[5px]" />
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => requestNav({ kind: 'new' })}
                          disabled={saving || importing || deleting}
                          className={TB_MENU_ITEM}
                          data-help-target="mainapp.resumes.resumeN.resumemenu.newresume"
                          data-testid="new-resume-item"
                          title="Create a new resume"
                        >
                          <Plus className="w-[15px] h-[15px] text-gray-500 flex-shrink-0" />
                          <span>New Resume</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleClose}
              disabled={importing}
              data-help-target="mainapp.resumes.resumeN.close"
              className="w-[34px] h-[34px] grid place-items-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* Import Progress Overlay */}
        {importing && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-10" role="status" aria-label="Analyzing resume file">
            <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
            <p className="text-gray-700 font-medium">Analyzing resume file...</p>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".yaml,.yml,.json,.pdf"
          onChange={handleFileSelect}
          className="hidden"
        />

        {loading ? (
          <div className="flex items-center justify-center py-12" role="status" aria-label="Loading resume">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="relative flex-1 flex min-h-0">
          {/* Left rail: section nav (bands + drag reorder). */}
          <SectionNavPanel
            orderedSections={orderedSections}
            getLabel={sectionLabel}
            genTypes={sectionGenTypes}
            getState={navRowState}
            attentionCounts={navAttentionCounts}
            recommendationCount={pendingRecs.length}
            recommendationSections={getRecSectionCounts()}
            onShowRecommendations={scrollToRecommendations}
            onReorder={(next) => { setSectionOrder(['contact', ...next]); touchSectionSettings(); }}
            onMove={moveSection}
            onCycleGenType={cycleSectionGenType}
            activeKey={activeNavKey}
            onNavigate={scrollToNavKey}
            progress={navProgress}
            score={scorecardScore}
            onShowScorecard={resumename ? () => setShowScorecard(true) : undefined}
            storageKey={resumename || '__new__'}
          />

          <div ref={scrollRef} className="flex-1 min-w-0 overflow-y-auto p-4 space-y-4">
            {tagGenPhase === 'generating' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm flex items-start gap-2">
                <Loader2 className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin" />
                <span>
                  Tag generation is running — this usually takes about 60 seconds.
                  Items being tagged are marked below; their tags will appear automatically when ready, and you can keep editing in the meantime.
                  <br />
                  <span className="text-yellow-700">Generated tags are AI-produced — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'done' ? (
              <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded-lg text-sm flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Tag generation complete — tags have been added to your resume.
                  <br />
                  <span className="text-green-700">Generated tags are AI-produced — please verify accuracy.</span>
                </span>
              </div>
            ) : tagGenPhase === 'timeout' ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm">
                Tag generation is taking longer than usual. Close and reopen this resume in a moment to see the tags.
              </div>
            ) : warning ? (
              <div className="p-3 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm whitespace-pre-line">{warning}</div>
            ) : null}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
            )}

            {/* AI Review status banners. The Review trigger and the Scorecard
                chip live in the TOP BAR; only the banners stay in the pane. */}
            {resumename && (
              <div className="space-y-2">
                {/* Pending recommendations banner */}
                {hasRecommendations && (
                  <div data-review-anchor className="border border-purple-200 bg-purple-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">
                          {pendingRecs.length} pending recommendation{pendingRecs.length !== 1 ? 's' : ''}. Scroll down to review inline recommendations.
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={applyAllRecommendations}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Apply All
                        </button>
                        <button
                          onClick={dismissAllRecommendations}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                        >
                          Dismiss All
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {getRecSectionCounts().map(({ section, count }) => (
                        <span key={section} className="text-xs bg-white border border-purple-200 text-purple-700 px-2 py-0.5 rounded">
                          {section}: {count}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs mt-2 text-purple-600">
                      Remember: this resume will be customized per job, based on the job description. So while it&apos;s important to get a solid foundation, further AI customization is applied on top of this &quot;core&quot; resume.
                    </p>
                  </div>
                )}

                {/* "All implemented" banner — shown once, then suppressed on reopen */}
                {showAllImplemented && (
                  <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        All recommendations have been implemented!
                      </span>
                    </div>
                    <p className="text-xs mt-2 text-green-600">
                      Remember: this resume will be customized per job, based on the job description. So while it&apos;s important to get a solid foundation, further AI customization is applied on top of this &quot;core&quot; resume.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Basics — the resume's master URL and the professional name, one card. */}
            <div data-nav-key="basics" data-help-target="mainapp.resumes.resumeN.basics" className={`${SECTION_CARD} space-y-4`}>
              {/* Master Resume URL — always published, no opt-out */}
              <div className="space-y-2">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    This Resume&apos;s Master URL
                  </label>
                  <div className="flex items-center gap-0">
                    <span className="inline-flex items-center px-3 py-2 rounded-l-lg border border-r-0 border-gray-300 bg-gray-100 text-gray-500 text-sm select-all">
                      https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me/
                    </span>
                    <input
                      type="text"
                      value={masterResumePath}
                      onChange={(e) => {
                        // Only allow URL-safe characters
                        const val = e.target.value.replace(/[^a-zA-Z0-9_\-]/g, '');
                        setMasterResumePath(val);
                      }}
                      data-help-target="mainapp.resumes.resumeN.masterurl"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg text-sm"
                      placeholder="(optional path, e.g. solution_engineer)"
                    />
                    {resumename && user?.username && (
                      <a
                        href={`https://${user.username.toLowerCase()}.skillsnap.me/${masterResumePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 ml-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 text-sm whitespace-nowrap"
                        title="View Resume"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Resume</span>
                      </a>
                    )}
                  </div>
                  {/* Duplicate path warning */}
                  {(() => {
                    const normalizedPath = masterResumePath.toLowerCase();
                    const duplicate = otherResumePaths.find(
                      (r) => (r.path || '').toLowerCase() === normalizedPath
                    );
                    if (duplicate) {
                      return (
                        <div className="flex items-center gap-1 text-xs text-amber-600">
                          <AlertTriangle className="w-3 h-3" />
                          <span>
                            Resume &quot;{duplicate.resumename}&quot; also uses this path. Most recent save wins &mdash; only one will be displayed.
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  {!resumename && (
                    <p className="text-xs text-gray-500 italic">
                      Save this resume once before you can view the resume URL.
                    </p>
                  )}
                  {masterResumePath === '' && resumename && (
                    <p className="text-xs text-gray-500">
                      Leaving the path blank will publish at the root: https://{user?.username?.toLowerCase() || 'your_name'}.skillsnap.me
                    </p>
                  )}
                </div>
              </div>

              {/* Full Professional Name */}
              <div>
                <label className="block text-base font-bold text-gray-700 mb-1">
                  Your Full Professional Name *
                </label>
                <input
                  type="text"
                  value={resume.name}
                  onChange={(e) => setResume({
                    ...resume,
                    name: e.target.value
                  })}
                  data-help-target="mainapp.resumes.resumeN.profname"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="John Doe"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Define Synonyms */}
            <div data-nav-key="synonyms" data-help-target="mainapp.resumes.resumeN.synonyms" className={SECTION_CARD}>
              <label className="block text-base font-bold text-gray-700 mb-1">Define Synonyms</label>
              <p className="text-xs text-gray-500 mb-2">
                Optional. Each set are words or phrases that mean the same thing on your resume
                (e.g. &quot;Software Developer&quot; and &quot;Software Engineer&quot; and &quot;Programmer&quot;).
                When generating a bespoke resume, SkillSnap aligns your wording to whichever term the job description prefers.
                {' '}This only affects language in the Summary, Experience Bullets, and Skills sections.
              </p>
              <div className="space-y-2 mb-3">
                {(resume.synonyms || []).map((set, i) => {
                  const draftVal = synonymDrafts[set.id] ?? set.terms.join(' // ');
                  const termCount = parseSynonymTerms(draftVal).length;
                  const conflicted = conflictSetIds.has(set.id);
                  return (
                    <div key={set.id}>
                      <div className={`flex items-center gap-2 p-2 rounded ${conflicted ? 'bg-red-50 ring-2 ring-red-400' : 'bg-gray-50'}`}>
                        <input
                          type="text"
                          value={draftVal}
                          onChange={(e) => setSynonymDrafts(prev => ({ ...prev, [set.id]: e.target.value }))}
                          onBlur={() => commitSynonymDraft(set.id)}
                          onKeyDown={(e) => e.key === 'Enter' && commitSynonymDraft(set.id)}
                          className={`flex-1 px-2 py-1 border rounded text-sm ${conflicted ? 'border-red-300' : 'border-gray-300'}`}
                          placeholder="Software Developer // Software Engineer // Programmer"
                          autoComplete="off"
                        />
                        <button onClick={() => removeSynonymSet(set.id)} className="text-gray-500 hover:text-red-500" title="Remove synonym set">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {termCount === 1 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600 mt-0.5 ml-1">
                          <AlertTriangle className="w-3 h-3" />
                          <span>A synonym set needs at least 2 terms (separate with &quot;//&quot;) — this set will be ignored.</span>
                        </div>
                      )}
                      {renderInlineRecs(`synonyms[${i}]`)}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">New synonym set (separate terms with &quot;//&quot;)</label>
                  <input
                    type="text"
                    value={newSynonymTerms}
                    onChange={(e) => setNewSynonymTerms(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addSynonymSet()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Software Developer // Software Engineer // Programmer"
                    autoComplete="off"
                  />
                </div>
                <button
                  onClick={addSynonymSet}
                  disabled={parseSynonymTerms(newSynonymTerms).length < 2}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40"
                  title="Add synonym set"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {renderInlineRecs('synonyms')}
            </div>

            {/* Contact Information */}
            <div data-nav-key="contact" data-help-target="mainapp.resumes.resumeN.contact" className={SECTION_CARD}>
              <div className={sectionGenTypes.contact === 'omit' ? 'opacity-50' : ''}>
                {renderSectionHeader('contact', 'mb-2')}
                <div className="space-y-2 mb-3">
                  {resume.contact.map((item, i) => (
                    <div key={i}>
                      <div className={`flex items-center gap-2 p-2 bg-gray-50 rounded${item.include === '0' ? ' opacity-40' : ''}`}>
                        <input
                          type="checkbox"
                          checked={item.include !== '0'}
                          onChange={(e) => updateContactItem(i, { include: e.target.checked ? '1' : '0' })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 shrink-0"
                          title={item.include === '0' ? 'Excluded from generation' : 'Included in generation'}
                        />
                        {editingContactIndex === i ? (
                          <div className="flex-1 flex gap-2 items-center">
                            <div className="relative w-20">
                              <select value={item.icon} onChange={(e) => updateContactItem(i, { icon: e.target.value as ContactItem['icon'] })} className="w-full px-2 py-1 border border-gray-300 rounded text-sm appearance-none pl-7">
                                {sortedIconOptions.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                              </select>
                              <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                            </div>
                            <input type="text" value={item.title} onFocus={() => setEditContactAutoFill(!item.url)} onChange={(e) => { const updates: Partial<ContactItem> = { title: e.target.value }; if (editContactAutoFill) { const auto = autoFillContactUrl(item.icon, e.target.value); if (auto) updates.url = auto; } updateContactItem(i, updates); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="Title" autoComplete="off" />
                            <input type="text" value={item.url || ''} onChange={(e) => { setEditContactAutoFill(false); updateContactItem(i, { url: e.target.value }); }} className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm" placeholder="URL" autoComplete="off" />
                          </div>
                        ) : (
                          <>
                            <img src={getIconSvg(item.icon)} alt="" className="w-4 h-4" />
                            <span className="flex-1 text-sm truncate">
                              {item.title}
                              {item.url && <span className="text-gray-500 ml-2 text-xs">({item.url})</span>}
                            </span>
                          </>
                        )}
                        <button onClick={() => { setEditingContactIndex(editingContactIndex === i ? null : i); setEditContactAutoFill(false); }} className="text-gray-500 hover:text-gray-600">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveContactItem(i, 'up')} disabled={i === 0} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button onClick={() => moveContactItem(i, 'down')} disabled={i === resume.contact.length - 1} className="text-gray-500 hover:text-gray-600 disabled:opacity-30">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => removeContactItem(i)} className="text-gray-500 hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {renderInlineRecs(`contacts[${i}]`)}
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 items-end">
                  <div className="w-20">
                    <label className="block text-xs text-gray-500 mb-1">Icon</label>
                    <div className="relative">
                      <select
                        value={newContactIcon}
                        onChange={(e) => setNewContactIcon(e.target.value as ContactItem['icon'])}
                        className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm appearance-none pl-8"
                      >
                        {sortedIconOptions.map(i => (
                          <option key={i.value} value={i.value}>{i.label}</option>
                        ))}
                      </select>
                      <img src={getIconSvg(newContactIcon)} alt="" className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Title</label>
                    <input type="text" value={newContactTitle} onFocus={() => setNewContactAutoFill(!newContactUrl)} onChange={(e) => { setNewContactTitle(e.target.value); if (newContactAutoFill) { const auto = autoFillContactUrl(newContactIcon, e.target.value); if (auto) setNewContactUrl(auto); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="name displayed" autoComplete="off" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">URL / Link</label>
                    <input type="text" value={newContactUrl} onChange={(e) => { setNewContactAutoFill(false); setNewContactUrl(e.target.value); }} onKeyDown={(e) => e.key === 'Enter' && addContactItem()} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder={getUrlPlaceholder(newContactIcon)} autoComplete="off" />
                  </div>
                  <button onClick={addContactItem} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Demographics — optional, never appears on the resume. Available in
                the Chrome Extension to accelerate applications. */}
            {(() => {
              const demo: Demographics = { ...emptyDemographics, ...(resume.demographics || {}) };
              const country = (demo.location_country || '').trim() || 'your country';
              // Renders a labeled dropdown; when "Other" is chosen, reveals a
              // free-text input bound to `${field}_other`.
              const renderSelect = (
                label: string,
                field: keyof Demographics,
                options: string[],
                withOther: boolean,
              ) => (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">{label}</label>
                  <select
                    value={(demo[field] as string) || ''}
                    onChange={(e) => updateDemographics({ [field]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Select…</option>
                    {options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                  {withOther && demo[field] === 'Other' && (
                    <input
                      type="text"
                      value={(demo[`${field}_other` as keyof Demographics] as string) || ''}
                      onChange={(e) => updateDemographics({ [`${field}_other`]: e.target.value })}
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Please specify"
                    />
                  )}
                </div>
              );
              return (
                <div data-nav-key="demographics" data-help-target="mainapp.resumes.resumeN.demographics" className={SECTION_CARD}>
                  <label className="block text-base font-bold text-gray-700 mb-1">Demographics</label>
                  <p className="text-xs text-gray-500 mb-3">
                    Optional. Does not appear on your resume — available in the SkillSnap Chrome
                    Extension to accelerate your application process.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Location Country</label>
                      <input
                        type="text"
                        value={demo.location_country || ''}
                        onChange={(e) => updateDemographics({ location_country: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        placeholder="USA"
                      />
                    </div>
                    {renderSelect(`Authorized to work in ${country}?`, 'authorized_to_work', YES_NO_OPTIONS, false)}
                    {renderSelect('Require Visa Sponsorship?', 'require_visa_sponsorship', YES_NO_OPTIONS, false)}
                    {renderSelect('Gender', 'gender', GENDER_OPTIONS, true)}
                    {renderSelect('Race', 'race', RACE_OPTIONS, true)}
                    {renderSelect('Veteran', 'veteran', VET_DIS_OPTIONS, true)}
                    {renderSelect('Disabled', 'disabled', VET_DIS_OPTIONS, true)}
                  </div>
                </div>
              );
            })()}

            {/* Provide Writing Sample (per-user voice fingerprint) */}
            <div data-nav-key="writingsample" data-help-target="mainapp.resumes.resumeN.writingsample" className={SECTION_CARD}>
              <label className="block text-base font-bold text-gray-700 mb-1">
                Provide Writing Sample <span className="font-normal text-gray-400">(Optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Complete a short writing exercise and SkillSnap generates a &quot;fingerprint&quot; of your
                written voice — abstract traits like rhythm and punctuation to keep generated content
                sounding more like you.
              </p>
              {writingFingerprint ? (
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Done state: same button, grey, still opens the modal (redo). */}
                  <button
                    type="button"
                    onClick={() => setShowWritingModal(true)}
                    data-help-target="mainapp.resumes.resumeN.writingsample.open"
                    className="px-3 py-1.5 text-sm bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300"
                    title={writingFingerprint.created_at
                      ? `Fingerprint captured ${new Date(writingFingerprint.created_at).toLocaleDateString()} — click to redo`
                      : 'Fingerprint on file — click to redo'}
                  >
                    Complete Writing Sample - Done!
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteFingerprint}
                    disabled={deletingFingerprint}
                    data-help-target="mainapp.resumes.resumeN.writingsample.delete"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:text-red-600 hover:border-red-300 hover:bg-red-50 disabled:opacity-40"
                    title="Remove voice fingerprint"
                  >
                    {deletingFingerprint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete Writing Sample
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowWritingModal(true)}
                  data-help-target="mainapp.resumes.resumeN.writingsample.open"
                  className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Complete Writing Sample
                </button>
              )}
            </div>

            {/* Reorderable sections — order comes from the per-resume
                section settings draft (saved with the resume). */}
            {orderedSections.map((component) => (
              <div key={component} data-nav-key={component} className={SECTION_CARD}>
                {renderSection(component)}
              </div>
            ))}

            {/* Cover Letter — pinned last (it isn't a rendered resume section,
                so it has no content editor, no reorder arrows and no custom
                name; only the generation type is set here). */}
            <div data-nav-key="coverletter" data-help-target="mainapp.resumes.resumeN.coverletter" className={SECTION_CARD}>
              <div className={sectionGenTypes.coverletter === 'omit' ? 'opacity-50' : ''}>
                {renderSectionHeader('coverletter', 'mb-1', { editableTitle: false })}
                <p className="text-xs text-gray-500 mb-2">
                  {sectionGenTypes.coverletter === 'omit'
                    ? 'Cover letters will not be generated for jobs using this resume.'
                    : 'Cover letters are generated per job from this resume.'}
                </p>
              </div>
            </div>


          </div>

          {/* Live preview slides out from the RIGHT, over the right edge of the
              editor content — the left nav rail stays visible. */}
          {previewOpen && (
            <ResumePreviewPanel
              resume={resume}
              orderedSections={orderedSections}
              getLabel={sectionLabel}
              genTypes={sectionGenTypes}
              activeKey={activeNavKey}
              onClose={() => setPreviewOpen(false)}
            />
          )}
          </div>
        )}

        {/* ── Bottom bar: save state · section stepper · save actions ── */}
        <footer className="flex-shrink-0 min-h-[60px] flex items-center gap-3 px-[18px] py-2 bg-white border-t border-gray-200">
          <span className="flex items-center gap-2 text-[12.5px] text-gray-500" data-help-target="mainapp.resumes.resumeN.savestate" data-testid="save-state">
            <span className={`w-[7px] h-[7px] rounded-full flex-shrink-0 ${isDirty ? 'bg-amber-500' : 'bg-green-700'}`} aria-hidden="true" />
            {isDirty ? (
              <span className="font-semibold text-amber-700">Unsaved changes</span>
            ) : (
              <span>
                <span className="font-semibold text-gray-700">All changes saved</span>
                {lastSavedLabel ? ` · ${lastSavedLabel}` : ''}
              </span>
            )}
          </span>

          {navIndex >= 0 && navKeyOrder.length > 0 && (
            <>
              <span className={`${TB_VR} hidden lg:block`} aria-hidden="true" />
              <span className="hidden lg:flex items-center gap-1.5 text-[12.5px] text-gray-500" data-help-target="mainapp.resumes.resumeN.sectionposition" data-testid="section-position">
                <span className="font-semibold text-gray-700">Section {navIndex + 1} of {navKeyOrder.length}</span>
                <span>· {navKeyLabel(navKeyOrder[navIndex])}</span>
              </span>
            </>
          )}

          <span className="flex-1" />

          <span className="hidden lg:flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => navIndex > 0 && scrollToNavKey(navKeyOrder[navIndex - 1])}
              disabled={navIndex <= 0}
              data-help-target="mainapp.resumes.resumeN.prevsection"
              className={TB_GHOST}
              title="Scroll to the previous section"
            >
              <ArrowLeft className="w-[15px] h-[15px]" />
              <span className="hidden xl:inline">Previous section</span>
            </button>
            <button
              type="button"
              onClick={() => navIndex >= 0 && navIndex < navKeyOrder.length - 1 && scrollToNavKey(navKeyOrder[navIndex + 1])}
              disabled={navIndex < 0 || navIndex >= navKeyOrder.length - 1}
              data-help-target="mainapp.resumes.resumeN.nextsection"
              className={TB_GHOST}
              title="Scroll to the next section"
            >
              <span className="hidden xl:inline">Next section</span>
              <ArrowRight className="w-[15px] h-[15px]" />
            </button>
          </span>

          <span className={TB_VR} aria-hidden="true" />

          <button
            onClick={handleClose}
            disabled={importing}
            data-help-target="mainapp.resumes.resumeN.cancel"
            className={TB_GHOST}
            title="Close without saving"
          >
            Cancel
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={saving || loading || importing}
            title="Save without closing — useful when you want tag regeneration to run and then keep editing"
            data-help-target="mainapp.resumes.resumeN.save"
            className={TB_OUT}
          >
            {saving ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Save className="w-[15px] h-[15px]" />}
            Save
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || loading || importing}
            data-help-target="mainapp.resumes.resumeN.saveclose"
            className={TB_PRIMARY}
          >
            {saving ? <Loader2 className="w-[15px] h-[15px] animate-spin" /> : <Check className="w-[15px] h-[15px]" />}
            Save and Close
          </button>
        </footer>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Delete Resume
              </h3>
              <p className="text-sm text-gray-600 text-center mb-1">
                Are you sure you want to delete <span className="font-medium">"{resumename}"</span>?
              </p>
              <p className="text-sm text-red-600 text-center font-medium">
                This action cannot be undone.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteResume}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved-changes guard for Resume ▾ → Switch resumes / New Resume.
          Both leave the current draft behind, so a dirty editor confirms. */}
      {pendingNav && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]" role="dialog" aria-modal="true" data-testid="switch-unsaved-confirm">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-amber-100 rounded-full mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                Discard unsaved changes?
              </h3>
              <p className="text-sm text-gray-600 text-center">
                {pendingNav.kind === 'switch' ? (
                  <>You have unsaved edits to <span className="font-medium">"{resumename}"</span>. Switching to <span className="font-medium">"{pendingNav.resumename}"</span> will discard them.</>
                ) : (
                  <>You have unsaved edits to <span className="font-medium">"{resumename}"</span>. Starting a new resume will discard them.</>
                )}
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={() => setPendingNav(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Keep editing
              </button>
              <button
                onClick={() => runNav(pendingNav)}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Discard and {pendingNav.kind === 'switch' ? 'switch' : 'continue'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradePromptModal
        isOpen={showAiUpgradeModal}
        onClose={() => setShowAiUpgradeModal(false)}
        limitType="ai"
        onNavigateToSettings={() => {
          setShowAiUpgradeModal(false);
          onClose();
        }}
      />

      <ScorecardModal
        isOpen={showScorecard}
        onClose={() => setShowScorecard(false)}
        resume={resume}
        resumename={resumename}
        onSaveItem={handleScorecardItemSave}
        onResolvedChange={handleScorecardResolvedChange}
      />

      <WritingSampleModal
        isOpen={showWritingModal}
        onClose={() => setShowWritingModal(false)}
        onSuccess={reloadWritingFingerprint}
      />
    </div>
  );
}
